import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ArchiveInvoiceApplicationErrors,
  ArchiveInvoiceResponse,
  BulkCreateInvoicesApplicationErrors,
  BulkCreateInvoicesOptions,
  BulkCreateInvoicesResponse,
  BulkCreateInvoicesValidationErrors,
  BulkDeleteInvoicesResponse,
  BulkUpdateInvoiceTagsApplicationErrors,
  BulkUpdateInvoiceTagsByFilterApplicationErrors,
  BulkUpdateInvoiceTagsByFilterOptions,
  BulkUpdateInvoiceTagsByFilterResponse,
  BulkUpdateInvoiceTagsOptions,
  BulkUpdateInvoiceTagsResponse,
  BulkUpdateInvoicesApplicationErrors,
  BulkUpdateInvoicesOptions,
  BulkUpdateInvoicesResponse,
  BulkUpdateInvoicesValidationErrors,
  CalculateInvoiceApplicationErrors,
  CalculateInvoiceOptions,
  CalculateInvoiceResponse,
  CreateInvoiceApplicationErrors,
  CreateInvoiceValidationErrors,
  DeleteInvoiceApplicationErrors,
  EnableInvoicePaymentsApplicationErrors,
  EnableInvoicePaymentsResponse,
  GeneratePdfDocumentApplicationErrors,
  GeneratePdfDocumentResponse,
  GenerateReceiptResponse,
  GetInvoiceApplicationErrors,
  GetLatestInvoiceNumberOptions,
  GetLatestInvoiceNumberResponse,
  InitiatePaymentApplicationErrors,
  InitiatePaymentResponse,
  Invoice,
  InvoiceCreatedEnvelope,
  InvoiceDeletedEnvelope,
  InvoiceQuery,
  InvoiceSearch,
  InvoiceSentEnvelope,
  InvoiceTagsModifiedEnvelope,
  InvoiceUpdatedEnvelope,
  InvoicesQueryBuilder,
  MarkInvoiceAsSentResponse,
  MarkInvoiceAsViewedResponse,
  MaskedInvoice,
  PublishInvoiceApplicationErrors,
  PublishInvoiceResponse,
  QueryInvoicesApplicationErrors,
  QueryInvoicesResponse,
  SearchInvoicesResponse,
  SendInvoiceApplicationErrors,
  SendInvoiceOptions,
  SendInvoiceResponse,
  UnarchiveInvoiceApplicationErrors,
  UnarchiveInvoiceResponse,
  UpdateInvoice,
  UpdateInvoiceApplicationErrors,
  UpdateInvoiceValidationErrors,
  VoidInvoiceApplicationErrors,
  VoidInvoiceResponse,
  archiveInvoice as universalArchiveInvoice,
  bulkCreateInvoices as universalBulkCreateInvoices,
  bulkDeleteInvoices as universalBulkDeleteInvoices,
  bulkUpdateInvoiceTags as universalBulkUpdateInvoiceTags,
  bulkUpdateInvoiceTagsByFilter as universalBulkUpdateInvoiceTagsByFilter,
  bulkUpdateInvoices as universalBulkUpdateInvoices,
  calculateInvoice as universalCalculateInvoice,
  createInvoice as universalCreateInvoice,
  deleteInvoice as universalDeleteInvoice,
  enableInvoicePayments as universalEnableInvoicePayments,
  generatePdfDocument as universalGeneratePdfDocument,
  generateReceipt as universalGenerateReceipt,
  getInvoice as universalGetInvoice,
  getLatestInvoiceNumber as universalGetLatestInvoiceNumber,
  initiatePayment as universalInitiatePayment,
  markInvoiceAsSent as universalMarkInvoiceAsSent,
  markInvoiceAsViewed as universalMarkInvoiceAsViewed,
  publishInvoice as universalPublishInvoice,
  queryInvoices as universalQueryInvoices,
  searchInvoices as universalSearchInvoices,
  sendInvoice as universalSendInvoice,
  typedQueryInvoices as universalTypedQueryInvoices,
  unarchiveInvoice as universalUnarchiveInvoice,
  updateInvoice as universalUpdateInvoice,
  voidInvoice as universalVoidInvoice,
} from './invoices-invoices-v4-invoice-invoices.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/get-paid' };

export function createInvoice(httpClient: HttpClient): CreateInvoiceSignature {
  return (
    invoice: NonNullablePaths<
      Invoice,
      | `currency`
      | `customerInfo`
      | `customerInfo.contactId`
      | `issueDate`
      | `lineItems`
      | `lineItems.${number}.catalogItem.catalogReference`
      | `lineItems.${number}.catalogItem.catalogReference.appId`
      | `lineItems.${number}.catalogItem.catalogReference.catalogItemId`
      | `lineItems.${number}.customItem.name`
      | `lineItems.${number}.customItem.price`
      | `lineItems.${number}.customItem.quantity`
      | `lineItems.${number}.lineItemType`
      | `reference`
      | `reference.orderReference.orderId`
      | `reference.referenceType`
      | `shipmentInfo.code`
      | `shipmentInfo.price`
      | `shippingInfo.contactDetails`
      | `shippingInfo.contactDetails.email`
      | `shippingInfo.shippingAddress`,
      6
    >
  ) =>
    universalCreateInvoice(
      invoice,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateInvoiceSignature {
  /**
   * Creates an invoice.
   *
   *
   * To create multiple invoices in a single API call, call [Bulk Create Invoices](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/bulk-create-invoices).
   * @param - Invoice to create.
   * @returns Created invoice.
   */
  (
    invoice: NonNullablePaths<
      Invoice,
      | `currency`
      | `customerInfo`
      | `customerInfo.contactId`
      | `issueDate`
      | `lineItems`
      | `lineItems.${number}.catalogItem.catalogReference`
      | `lineItems.${number}.catalogItem.catalogReference.appId`
      | `lineItems.${number}.catalogItem.catalogReference.catalogItemId`
      | `lineItems.${number}.customItem.name`
      | `lineItems.${number}.customItem.price`
      | `lineItems.${number}.customItem.quantity`
      | `lineItems.${number}.lineItemType`
      | `reference`
      | `reference.orderReference.orderId`
      | `reference.referenceType`
      | `shipmentInfo.code`
      | `shipmentInfo.price`
      | `shippingInfo.contactDetails`
      | `shippingInfo.contactDetails.email`
      | `shippingInfo.shippingAddress`,
      6
    >
  ): Promise<
    NonNullablePaths<
      Invoice,
      | `status`
      | `statusQualifiers`
      | `availableActions`
      | `sourceReference.appId`
      | `reference.standaloneReference.limitActions`
      | `reference.orderReference.orderId`
      | `reference.migratedReference.limitActions`
      | `reference.autoChargeReference.limitActions`
      | `reference.referenceType`
      | `regionalProperties.locale.languageCode`
      | `regionalProperties.language`
      | `numbering.displayNumber`
      | `currency`
      | `customerInfo.contactDetails.vatId._id`
      | `customerInfo.contactDetails.vatId.type`
      | `customerInfo.billingAddress.streetAddress.number`
      | `customerInfo.billingAddress.streetAddress.name`
      | `lineItems`
      | `lineItems.${number}.customItem.quantity`
      | `lineItems.${number}.customItem.price`
      | `lineItems.${number}.customItem.catalogReference.catalogItemId`
      | `lineItems.${number}.customItem.catalogReference.appId`
      | `lineItems.${number}.catalogItem.quantity`
      | `lineItems.${number}.catalogItem.itemType.preset`
      | `lineItems.${number}.catalogItem.itemType.custom`
      | `lineItems.${number}.lineItemType`
      | `shipmentInfo.logistics.pickupDetails.pickupMethod`
      | `shipmentInfo.taxInfo.taxBreakdown`
      | `shipmentInfo.taxInfo.taxBreakdown.${number}.name`
      | `shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
      | `shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
      | `shipmentInfo.totals.priceBeforeTax`
      | `shipmentInfo.totals.priceAfterTax`
      | `additionalFees`
      | `additionalFees.${number}.name`
      | `additionalFees.${number}.amount`
      | `discounts`
      | `discounts.${number}.fixedDiscount`
      | `discounts.${number}.percentageDiscount`
      | `discounts.${number}.discountType`
      | `discounts.${number}.appliedAmount`
      | `deposit.fixedDeposit`
      | `deposit.percentageDeposit`
      | `deposit.depositType`
      | `taxInfo.taxBreakdown`
      | `taxInfo.taxBreakdown.${number}.name`
      | `taxInfo.taxBreakdown.${number}.rate`
      | `taxInfo.taxBreakdown.${number}.taxableAmount`
      | `taxInfo.taxBreakdown.${number}.exemptAmount`
      | `taxInfo.taxBreakdown.${number}.taxAmount`
      | `taxInfo.taxExempt`
      | `payments`
      | `payments.${number}.regularPayment.paymentOrderId`
      | `payments.${number}.regularPayment.paymentMethod`
      | `payments.${number}.regularPayment.offlinePayment`
      | `payments.${number}.regularPayment.paymentStatus`
      | `payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
      | `payments.${number}.regularPayment.creditCardDetails.brand`
      | `payments.${number}.giftCardPayment.giftCardPaymentId`
      | `payments.${number}.giftCardPayment.appId`
      | `payments.${number}._id`
      | `payments.${number}.amount`
      | `payments.${number}.paymentType`
      | `payments.${number}.receiptType`
      | `payments.${number}.cashRounding.roundingAmount`
      | `payments.${number}.cashRounding.unroundedAmount`
      | `totals.subtotal`
      | `totals.total`
      | `totals.paidAmount`
      | `totals.balance`
      | `archived`
      | `customFields.headerCustomFields`
      | `customFields.headerCustomFields.${number}.value`
      | `customFields.businessCustomFields`
      | `customFields.customerCustomFields`
      | `customFields.footerCustomField.overridePresetContent`
      | `presetProperties.invoicePreset.name`
      | `presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
      | `presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
      | `presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
      | `presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
      | `presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
      | `presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
      | `presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
      | `presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
      | `presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
      | `presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
      | `presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
      | `presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
      | `presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
      | `presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
      | `presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
      | `presetProperties.invoicePreset.customFields.headerCustomFields`
      | `presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
      | `presetProperties.invoicePreset.customFields.businessCustomFields`
      | `presetProperties.invoicePreset.customFields.customerCustomFields`
      | `presetProperties.invoicePreset.customFields.headerPlaceholders`
      | `presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
      | `presetProperties.invoicePreset.customFields.customerPlaceholders`
      | `presetProperties.invoicePreset.defaultPreset`
      | `attachments`
      | `attachments.${number}.fileName`
      | `attachments.${number}.fileUrl`
      | `documentInfo.status`
      | `links.relativePath`
      | `businessLocation._id`
      | `activityInfo.sentToContactIds`
      | `hasSubscriptions`
      | `tags.privateTags.tagIds`,
      7
    > & {
      __applicationErrorsType?: CreateInvoiceApplicationErrors;
      __validationErrorsType?: CreateInvoiceValidationErrors;
    }
  >;
}

export function getInvoice(httpClient: HttpClient): GetInvoiceSignature {
  return (invoiceId: string) =>
    universalGetInvoice(
      invoiceId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetInvoiceSignature {
  /**
   * Retrieves an invoice.
   * @param - Invoice ID.
   * @returns Retrieved invoice.
   */
  (invoiceId: string): Promise<
    NonNullablePaths<
      Invoice,
      | `status`
      | `statusQualifiers`
      | `availableActions`
      | `sourceReference.appId`
      | `reference.standaloneReference.limitActions`
      | `reference.orderReference.orderId`
      | `reference.migratedReference.limitActions`
      | `reference.autoChargeReference.limitActions`
      | `reference.referenceType`
      | `regionalProperties.locale.languageCode`
      | `regionalProperties.language`
      | `numbering.displayNumber`
      | `currency`
      | `customerInfo.contactDetails.vatId._id`
      | `customerInfo.contactDetails.vatId.type`
      | `customerInfo.billingAddress.streetAddress.number`
      | `customerInfo.billingAddress.streetAddress.name`
      | `lineItems`
      | `lineItems.${number}.customItem.quantity`
      | `lineItems.${number}.customItem.price`
      | `lineItems.${number}.customItem.catalogReference.catalogItemId`
      | `lineItems.${number}.customItem.catalogReference.appId`
      | `lineItems.${number}.catalogItem.quantity`
      | `lineItems.${number}.catalogItem.itemType.preset`
      | `lineItems.${number}.catalogItem.itemType.custom`
      | `lineItems.${number}.lineItemType`
      | `shipmentInfo.logistics.pickupDetails.pickupMethod`
      | `shipmentInfo.taxInfo.taxBreakdown`
      | `shipmentInfo.taxInfo.taxBreakdown.${number}.name`
      | `shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
      | `shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
      | `shipmentInfo.totals.priceBeforeTax`
      | `shipmentInfo.totals.priceAfterTax`
      | `additionalFees`
      | `additionalFees.${number}.name`
      | `additionalFees.${number}.amount`
      | `discounts`
      | `discounts.${number}.fixedDiscount`
      | `discounts.${number}.percentageDiscount`
      | `discounts.${number}.discountType`
      | `discounts.${number}.appliedAmount`
      | `deposit.fixedDeposit`
      | `deposit.percentageDeposit`
      | `deposit.depositType`
      | `taxInfo.taxBreakdown`
      | `taxInfo.taxBreakdown.${number}.name`
      | `taxInfo.taxBreakdown.${number}.rate`
      | `taxInfo.taxBreakdown.${number}.taxableAmount`
      | `taxInfo.taxBreakdown.${number}.exemptAmount`
      | `taxInfo.taxBreakdown.${number}.taxAmount`
      | `taxInfo.taxExempt`
      | `payments`
      | `payments.${number}.regularPayment.paymentOrderId`
      | `payments.${number}.regularPayment.paymentMethod`
      | `payments.${number}.regularPayment.offlinePayment`
      | `payments.${number}.regularPayment.paymentStatus`
      | `payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
      | `payments.${number}.regularPayment.creditCardDetails.brand`
      | `payments.${number}.giftCardPayment.giftCardPaymentId`
      | `payments.${number}.giftCardPayment.appId`
      | `payments.${number}._id`
      | `payments.${number}.amount`
      | `payments.${number}.paymentType`
      | `payments.${number}.receiptType`
      | `payments.${number}.cashRounding.roundingAmount`
      | `payments.${number}.cashRounding.unroundedAmount`
      | `totals.subtotal`
      | `totals.total`
      | `totals.paidAmount`
      | `totals.balance`
      | `archived`
      | `customFields.headerCustomFields`
      | `customFields.headerCustomFields.${number}.value`
      | `customFields.businessCustomFields`
      | `customFields.customerCustomFields`
      | `customFields.footerCustomField.overridePresetContent`
      | `presetProperties.invoicePreset.name`
      | `presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
      | `presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
      | `presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
      | `presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
      | `presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
      | `presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
      | `presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
      | `presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
      | `presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
      | `presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
      | `presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
      | `presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
      | `presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
      | `presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
      | `presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
      | `presetProperties.invoicePreset.customFields.headerCustomFields`
      | `presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
      | `presetProperties.invoicePreset.customFields.businessCustomFields`
      | `presetProperties.invoicePreset.customFields.customerCustomFields`
      | `presetProperties.invoicePreset.customFields.headerPlaceholders`
      | `presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
      | `presetProperties.invoicePreset.customFields.customerPlaceholders`
      | `presetProperties.invoicePreset.defaultPreset`
      | `attachments`
      | `attachments.${number}.fileName`
      | `attachments.${number}.fileUrl`
      | `documentInfo.status`
      | `links.relativePath`
      | `businessLocation._id`
      | `activityInfo.sentToContactIds`
      | `hasSubscriptions`
      | `tags.privateTags.tagIds`,
      7
    > & {
      __applicationErrorsType?: GetInvoiceApplicationErrors;
    }
  >;
}

export function updateInvoice(httpClient: HttpClient): UpdateInvoiceSignature {
  return (
    _id: string,
    invoice: NonNullablePaths<
      UpdateInvoice,
      `lineItems.${number}.customItem.name` | `revision`,
      5
    >
  ) =>
    universalUpdateInvoice(
      _id,
      invoice,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateInvoiceSignature {
  /**
   * Updates an invoice.
   *
   *
   * To update multiple invoices in a single API call, call [Bulk Update Invoices](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/bulk-update-invoices).
   * @param - Invoice ID.
   * @returns Updated invoice.
   */
  (
    _id: string,
    invoice: NonNullablePaths<
      UpdateInvoice,
      `lineItems.${number}.customItem.name` | `revision`,
      5
    >
  ): Promise<
    NonNullablePaths<
      Invoice,
      | `status`
      | `statusQualifiers`
      | `availableActions`
      | `sourceReference.appId`
      | `reference.standaloneReference.limitActions`
      | `reference.orderReference.orderId`
      | `reference.migratedReference.limitActions`
      | `reference.autoChargeReference.limitActions`
      | `reference.referenceType`
      | `regionalProperties.locale.languageCode`
      | `regionalProperties.language`
      | `numbering.displayNumber`
      | `currency`
      | `customerInfo.contactDetails.vatId._id`
      | `customerInfo.contactDetails.vatId.type`
      | `customerInfo.billingAddress.streetAddress.number`
      | `customerInfo.billingAddress.streetAddress.name`
      | `lineItems`
      | `lineItems.${number}.customItem.quantity`
      | `lineItems.${number}.customItem.price`
      | `lineItems.${number}.customItem.catalogReference.catalogItemId`
      | `lineItems.${number}.customItem.catalogReference.appId`
      | `lineItems.${number}.catalogItem.quantity`
      | `lineItems.${number}.catalogItem.itemType.preset`
      | `lineItems.${number}.catalogItem.itemType.custom`
      | `lineItems.${number}.lineItemType`
      | `shipmentInfo.logistics.pickupDetails.pickupMethod`
      | `shipmentInfo.taxInfo.taxBreakdown`
      | `shipmentInfo.taxInfo.taxBreakdown.${number}.name`
      | `shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
      | `shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
      | `shipmentInfo.totals.priceBeforeTax`
      | `shipmentInfo.totals.priceAfterTax`
      | `additionalFees`
      | `additionalFees.${number}.name`
      | `additionalFees.${number}.amount`
      | `discounts`
      | `discounts.${number}.fixedDiscount`
      | `discounts.${number}.percentageDiscount`
      | `discounts.${number}.discountType`
      | `discounts.${number}.appliedAmount`
      | `deposit.fixedDeposit`
      | `deposit.percentageDeposit`
      | `deposit.depositType`
      | `taxInfo.taxBreakdown`
      | `taxInfo.taxBreakdown.${number}.name`
      | `taxInfo.taxBreakdown.${number}.rate`
      | `taxInfo.taxBreakdown.${number}.taxableAmount`
      | `taxInfo.taxBreakdown.${number}.exemptAmount`
      | `taxInfo.taxBreakdown.${number}.taxAmount`
      | `taxInfo.taxExempt`
      | `payments`
      | `payments.${number}.regularPayment.paymentOrderId`
      | `payments.${number}.regularPayment.paymentMethod`
      | `payments.${number}.regularPayment.offlinePayment`
      | `payments.${number}.regularPayment.paymentStatus`
      | `payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
      | `payments.${number}.regularPayment.creditCardDetails.brand`
      | `payments.${number}.giftCardPayment.giftCardPaymentId`
      | `payments.${number}.giftCardPayment.appId`
      | `payments.${number}._id`
      | `payments.${number}.amount`
      | `payments.${number}.paymentType`
      | `payments.${number}.receiptType`
      | `payments.${number}.cashRounding.roundingAmount`
      | `payments.${number}.cashRounding.unroundedAmount`
      | `totals.subtotal`
      | `totals.total`
      | `totals.paidAmount`
      | `totals.balance`
      | `archived`
      | `customFields.headerCustomFields`
      | `customFields.headerCustomFields.${number}.value`
      | `customFields.businessCustomFields`
      | `customFields.customerCustomFields`
      | `customFields.footerCustomField.overridePresetContent`
      | `presetProperties.invoicePreset.name`
      | `presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
      | `presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
      | `presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
      | `presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
      | `presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
      | `presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
      | `presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
      | `presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
      | `presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
      | `presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
      | `presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
      | `presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
      | `presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
      | `presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
      | `presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
      | `presetProperties.invoicePreset.customFields.headerCustomFields`
      | `presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
      | `presetProperties.invoicePreset.customFields.businessCustomFields`
      | `presetProperties.invoicePreset.customFields.customerCustomFields`
      | `presetProperties.invoicePreset.customFields.headerPlaceholders`
      | `presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
      | `presetProperties.invoicePreset.customFields.customerPlaceholders`
      | `presetProperties.invoicePreset.defaultPreset`
      | `attachments`
      | `attachments.${number}.fileName`
      | `attachments.${number}.fileUrl`
      | `documentInfo.status`
      | `links.relativePath`
      | `businessLocation._id`
      | `activityInfo.sentToContactIds`
      | `hasSubscriptions`
      | `tags.privateTags.tagIds`,
      7
    > & {
      __applicationErrorsType?: UpdateInvoiceApplicationErrors;
      __validationErrorsType?: UpdateInvoiceValidationErrors;
    }
  >;
}

export function deleteInvoice(httpClient: HttpClient): DeleteInvoiceSignature {
  return (invoiceId: string) =>
    universalDeleteInvoice(
      invoiceId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteInvoiceSignature {
  /**
   * Deletes an invoice.
   *
   *
   * Deleting an invoice permanently removes it from the invoice list. Only invoices in `DRAFT` status can be deleted.
   *
   * To delete multiple invoices in a single API call, call [Bulk Delete Invoices](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/bulk-delete-invoices).
   * @param - Invoice ID.
   */
  (invoiceId: string): Promise<
    void & {
      __applicationErrorsType?: DeleteInvoiceApplicationErrors;
    }
  >;
}

export function queryInvoices(httpClient: HttpClient): QueryInvoicesSignature {
  return () =>
    universalQueryInvoices(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryInvoicesSignature {
  /**
   * Retrieves a list of up to 1,000 invoices, given the provided paging, filtering, and sorting.
   *
   *
   * Query Invoices runs with these defaults, which you can override:
   *
   * - `createdDate` is sorted in `DESC` order
   * - `paging.limit` is `50`
   * - `paging.offset` is `0`
   *
   * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language), [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging), and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
   */
  (): InvoicesQueryBuilder;
}

export function typedQueryInvoices(
  httpClient: HttpClient
): TypedQueryInvoicesSignature {
  return (query: InvoiceQuery) =>
    universalTypedQueryInvoices(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryInvoicesSignature {
  /** */
  (query: InvoiceQuery): Promise<
    NonNullablePaths<
      QueryInvoicesResponse,
      | `invoices`
      | `invoices.${number}.status`
      | `invoices.${number}.sourceReference.appId`
      | `invoices.${number}.reference.standaloneReference.limitActions`
      | `invoices.${number}.reference.orderReference.orderId`
      | `invoices.${number}.reference.migratedReference.limitActions`
      | `invoices.${number}.reference.autoChargeReference.limitActions`
      | `invoices.${number}.reference.referenceType`
      | `invoices.${number}.regionalProperties.locale.languageCode`
      | `invoices.${number}.regionalProperties.language`
      | `invoices.${number}.numbering.displayNumber`
      | `invoices.${number}.currency`
      | `invoices.${number}.shipmentInfo.totals.priceBeforeTax`
      | `invoices.${number}.shipmentInfo.totals.priceAfterTax`
      | `invoices.${number}.deposit.fixedDeposit`
      | `invoices.${number}.deposit.percentageDeposit`
      | `invoices.${number}.deposit.depositType`
      | `invoices.${number}.taxInfo.taxExempt`
      | `invoices.${number}.totals.subtotal`
      | `invoices.${number}.totals.total`
      | `invoices.${number}.totals.paidAmount`
      | `invoices.${number}.totals.balance`
      | `invoices.${number}.archived`
      | `invoices.${number}.customFields.footerCustomField.overridePresetContent`
      | `invoices.${number}.presetProperties.invoicePreset.name`
      | `invoices.${number}.presetProperties.invoicePreset.defaultPreset`
      | `invoices.${number}.documentInfo.status`
      | `invoices.${number}.links.relativePath`
      | `invoices.${number}.businessLocation._id`
      | `invoices.${number}.hasSubscriptions`,
      6
    > & {
      __applicationErrorsType?: QueryInvoicesApplicationErrors;
    }
  >;
}

export function searchInvoices(
  httpClient: HttpClient
): SearchInvoicesSignature {
  return (search: InvoiceSearch) =>
    universalSearchInvoices(
      search,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchInvoicesSignature {
  /**
   * Retrieves a list of up to 100 invoices, given the provided filtering, search expression, sorting, and cursor paging.
   *
   *
   * Unlike [Query Invoices], Search Invoices supports free-text search across fields like `title`, `numbering.displayNumber`, and customer contact details. It also returns aggregation data.
   * @param - Search options.
   */
  (search: InvoiceSearch): Promise<
    NonNullablePaths<
      SearchInvoicesResponse,
      | `invoices`
      | `invoices.${number}.status`
      | `invoices.${number}.sourceReference.appId`
      | `invoices.${number}.reference.standaloneReference.limitActions`
      | `invoices.${number}.reference.orderReference.orderId`
      | `invoices.${number}.reference.migratedReference.limitActions`
      | `invoices.${number}.reference.autoChargeReference.limitActions`
      | `invoices.${number}.reference.referenceType`
      | `invoices.${number}.regionalProperties.locale.languageCode`
      | `invoices.${number}.regionalProperties.language`
      | `invoices.${number}.numbering.displayNumber`
      | `invoices.${number}.currency`
      | `invoices.${number}.shipmentInfo.totals.priceBeforeTax`
      | `invoices.${number}.shipmentInfo.totals.priceAfterTax`
      | `invoices.${number}.deposit.fixedDeposit`
      | `invoices.${number}.deposit.percentageDeposit`
      | `invoices.${number}.deposit.depositType`
      | `invoices.${number}.taxInfo.taxExempt`
      | `invoices.${number}.totals.subtotal`
      | `invoices.${number}.totals.total`
      | `invoices.${number}.totals.paidAmount`
      | `invoices.${number}.totals.balance`
      | `invoices.${number}.archived`
      | `invoices.${number}.customFields.footerCustomField.overridePresetContent`
      | `invoices.${number}.presetProperties.invoicePreset.name`
      | `invoices.${number}.presetProperties.invoicePreset.defaultPreset`
      | `invoices.${number}.documentInfo.status`
      | `invoices.${number}.links.relativePath`
      | `invoices.${number}.businessLocation._id`
      | `invoices.${number}.hasSubscriptions`
      | `aggregationData.results`
      | `aggregationData.results.${number}.scalar.type`
      | `aggregationData.results.${number}.scalar.value`
      | `aggregationData.results.${number}.name`
      | `aggregationData.results.${number}.type`
      | `aggregationData.results.${number}.fieldPath`,
      6
    >
  >;
}

export function sendInvoice(httpClient: HttpClient): SendInvoiceSignature {
  return (invoiceId: string, options?: SendInvoiceOptions) =>
    universalSendInvoice(
      invoiceId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SendInvoiceSignature {
  /**
   * Sends an invoice to one or more contacts.
   *
   *
   * Specify `contactIds` in the request to choose recipients. If `contactIds` isn't provided, the invoice is sent to the customer contact on the invoice.
   * @param - Invoice ID.
   */
  (invoiceId: string, options?: SendInvoiceOptions): Promise<
    NonNullablePaths<
      SendInvoiceResponse,
      | `invoice.status`
      | `invoice.statusQualifiers`
      | `invoice.availableActions`
      | `invoice.sourceReference.appId`
      | `invoice.reference.standaloneReference.limitActions`
      | `invoice.reference.orderReference.orderId`
      | `invoice.reference.migratedReference.limitActions`
      | `invoice.reference.autoChargeReference.limitActions`
      | `invoice.reference.referenceType`
      | `invoice.regionalProperties.locale.languageCode`
      | `invoice.regionalProperties.language`
      | `invoice.numbering.displayNumber`
      | `invoice.currency`
      | `invoice.customerInfo.contactDetails.vatId._id`
      | `invoice.customerInfo.contactDetails.vatId.type`
      | `invoice.customerInfo.billingAddress.streetAddress.number`
      | `invoice.customerInfo.billingAddress.streetAddress.name`
      | `invoice.lineItems`
      | `invoice.lineItems.${number}.customItem.quantity`
      | `invoice.lineItems.${number}.customItem.price`
      | `invoice.lineItems.${number}.customItem.catalogReference.catalogItemId`
      | `invoice.lineItems.${number}.customItem.catalogReference.appId`
      | `invoice.lineItems.${number}.catalogItem.quantity`
      | `invoice.lineItems.${number}.catalogItem.itemType.preset`
      | `invoice.lineItems.${number}.catalogItem.itemType.custom`
      | `invoice.lineItems.${number}.lineItemType`
      | `invoice.shipmentInfo.logistics.pickupDetails.pickupMethod`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.name`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
      | `invoice.shipmentInfo.totals.priceBeforeTax`
      | `invoice.shipmentInfo.totals.priceAfterTax`
      | `invoice.additionalFees`
      | `invoice.additionalFees.${number}.name`
      | `invoice.additionalFees.${number}.amount`
      | `invoice.discounts`
      | `invoice.discounts.${number}.fixedDiscount`
      | `invoice.discounts.${number}.percentageDiscount`
      | `invoice.discounts.${number}.discountType`
      | `invoice.discounts.${number}.appliedAmount`
      | `invoice.deposit.fixedDeposit`
      | `invoice.deposit.percentageDeposit`
      | `invoice.deposit.depositType`
      | `invoice.taxInfo.taxBreakdown`
      | `invoice.taxInfo.taxBreakdown.${number}.name`
      | `invoice.taxInfo.taxBreakdown.${number}.rate`
      | `invoice.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `invoice.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `invoice.taxInfo.taxBreakdown.${number}.taxAmount`
      | `invoice.taxInfo.taxExempt`
      | `invoice.payments`
      | `invoice.payments.${number}.regularPayment.paymentOrderId`
      | `invoice.payments.${number}.regularPayment.paymentMethod`
      | `invoice.payments.${number}.regularPayment.offlinePayment`
      | `invoice.payments.${number}.regularPayment.paymentStatus`
      | `invoice.payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
      | `invoice.payments.${number}.regularPayment.creditCardDetails.brand`
      | `invoice.payments.${number}.giftCardPayment.giftCardPaymentId`
      | `invoice.payments.${number}.giftCardPayment.appId`
      | `invoice.payments.${number}._id`
      | `invoice.payments.${number}.amount`
      | `invoice.payments.${number}.paymentType`
      | `invoice.payments.${number}.receiptType`
      | `invoice.payments.${number}.cashRounding.roundingAmount`
      | `invoice.payments.${number}.cashRounding.unroundedAmount`
      | `invoice.totals.subtotal`
      | `invoice.totals.total`
      | `invoice.totals.paidAmount`
      | `invoice.totals.balance`
      | `invoice.archived`
      | `invoice.customFields.headerCustomFields`
      | `invoice.customFields.headerCustomFields.${number}.value`
      | `invoice.customFields.businessCustomFields`
      | `invoice.customFields.customerCustomFields`
      | `invoice.customFields.footerCustomField.overridePresetContent`
      | `invoice.presetProperties.invoicePreset.name`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
      | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
      | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
      | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
      | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
      | `invoice.presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
      | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
      | `invoice.presetProperties.invoicePreset.customFields.businessCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.customerCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders`
      | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
      | `invoice.presetProperties.invoicePreset.customFields.customerPlaceholders`
      | `invoice.presetProperties.invoicePreset.defaultPreset`
      | `invoice.attachments`
      | `invoice.attachments.${number}.fileName`
      | `invoice.attachments.${number}.fileUrl`
      | `invoice.documentInfo.status`
      | `invoice.links.relativePath`
      | `invoice.businessLocation._id`
      | `invoice.activityInfo.sentToContactIds`
      | `invoice.hasSubscriptions`
      | `invoice.tags.privateTags.tagIds`
      | `sendResults`
      | `sendResults.${number}.contactId`
      | `sendResults.${number}.success`
      | `sendResults.${number}.errorType`,
      8
    > & {
      __applicationErrorsType?: SendInvoiceApplicationErrors;
    }
  >;
}

export function publishInvoice(
  httpClient: HttpClient
): PublishInvoiceSignature {
  return (invoiceId: string) =>
    universalPublishInvoice(
      invoiceId,
      // @ts-ignore
      { httpClient }
    );
}

interface PublishInvoiceSignature {
  /**
   * Publishes an invoice.
   *
   *
   * Transitions the invoice status from `DRAFT` to `PUBLISHED`, finalizing it and assigning an invoice number. For details on status transitions, see [Invoice Lifecycle](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/standalone-invoice-payment-lifecycle).
   * @param - Invoice ID.
   */
  (invoiceId: string): Promise<
    NonNullablePaths<
      PublishInvoiceResponse,
      | `invoice.status`
      | `invoice.statusQualifiers`
      | `invoice.availableActions`
      | `invoice.sourceReference.appId`
      | `invoice.reference.standaloneReference.limitActions`
      | `invoice.reference.orderReference.orderId`
      | `invoice.reference.migratedReference.limitActions`
      | `invoice.reference.autoChargeReference.limitActions`
      | `invoice.reference.referenceType`
      | `invoice.regionalProperties.locale.languageCode`
      | `invoice.regionalProperties.language`
      | `invoice.numbering.displayNumber`
      | `invoice.currency`
      | `invoice.customerInfo.contactDetails.vatId._id`
      | `invoice.customerInfo.contactDetails.vatId.type`
      | `invoice.customerInfo.billingAddress.streetAddress.number`
      | `invoice.customerInfo.billingAddress.streetAddress.name`
      | `invoice.lineItems`
      | `invoice.lineItems.${number}.customItem.quantity`
      | `invoice.lineItems.${number}.customItem.price`
      | `invoice.lineItems.${number}.customItem.catalogReference.catalogItemId`
      | `invoice.lineItems.${number}.customItem.catalogReference.appId`
      | `invoice.lineItems.${number}.catalogItem.quantity`
      | `invoice.lineItems.${number}.catalogItem.itemType.preset`
      | `invoice.lineItems.${number}.catalogItem.itemType.custom`
      | `invoice.lineItems.${number}.lineItemType`
      | `invoice.shipmentInfo.logistics.pickupDetails.pickupMethod`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.name`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
      | `invoice.shipmentInfo.totals.priceBeforeTax`
      | `invoice.shipmentInfo.totals.priceAfterTax`
      | `invoice.additionalFees`
      | `invoice.additionalFees.${number}.name`
      | `invoice.additionalFees.${number}.amount`
      | `invoice.discounts`
      | `invoice.discounts.${number}.fixedDiscount`
      | `invoice.discounts.${number}.percentageDiscount`
      | `invoice.discounts.${number}.discountType`
      | `invoice.discounts.${number}.appliedAmount`
      | `invoice.deposit.fixedDeposit`
      | `invoice.deposit.percentageDeposit`
      | `invoice.deposit.depositType`
      | `invoice.taxInfo.taxBreakdown`
      | `invoice.taxInfo.taxBreakdown.${number}.name`
      | `invoice.taxInfo.taxBreakdown.${number}.rate`
      | `invoice.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `invoice.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `invoice.taxInfo.taxBreakdown.${number}.taxAmount`
      | `invoice.taxInfo.taxExempt`
      | `invoice.payments`
      | `invoice.payments.${number}.regularPayment.paymentOrderId`
      | `invoice.payments.${number}.regularPayment.paymentMethod`
      | `invoice.payments.${number}.regularPayment.offlinePayment`
      | `invoice.payments.${number}.regularPayment.paymentStatus`
      | `invoice.payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
      | `invoice.payments.${number}.regularPayment.creditCardDetails.brand`
      | `invoice.payments.${number}.giftCardPayment.giftCardPaymentId`
      | `invoice.payments.${number}.giftCardPayment.appId`
      | `invoice.payments.${number}._id`
      | `invoice.payments.${number}.amount`
      | `invoice.payments.${number}.paymentType`
      | `invoice.payments.${number}.receiptType`
      | `invoice.payments.${number}.cashRounding.roundingAmount`
      | `invoice.payments.${number}.cashRounding.unroundedAmount`
      | `invoice.totals.subtotal`
      | `invoice.totals.total`
      | `invoice.totals.paidAmount`
      | `invoice.totals.balance`
      | `invoice.archived`
      | `invoice.customFields.headerCustomFields`
      | `invoice.customFields.headerCustomFields.${number}.value`
      | `invoice.customFields.businessCustomFields`
      | `invoice.customFields.customerCustomFields`
      | `invoice.customFields.footerCustomField.overridePresetContent`
      | `invoice.presetProperties.invoicePreset.name`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
      | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
      | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
      | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
      | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
      | `invoice.presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
      | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
      | `invoice.presetProperties.invoicePreset.customFields.businessCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.customerCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders`
      | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
      | `invoice.presetProperties.invoicePreset.customFields.customerPlaceholders`
      | `invoice.presetProperties.invoicePreset.defaultPreset`
      | `invoice.attachments`
      | `invoice.attachments.${number}.fileName`
      | `invoice.attachments.${number}.fileUrl`
      | `invoice.documentInfo.status`
      | `invoice.links.relativePath`
      | `invoice.businessLocation._id`
      | `invoice.activityInfo.sentToContactIds`
      | `invoice.hasSubscriptions`
      | `invoice.tags.privateTags.tagIds`,
      8
    > & {
      __applicationErrorsType?: PublishInvoiceApplicationErrors;
    }
  >;
}

export function voidInvoice(httpClient: HttpClient): VoidInvoiceSignature {
  return (invoiceId: string) =>
    universalVoidInvoice(
      invoiceId,
      // @ts-ignore
      { httpClient }
    );
}

interface VoidInvoiceSignature {
  /**
   * Voids an invoice.
   *
   *
   * Transitions the invoice status to `VOIDED`, canceling it and making it no longer valid. Any payments made before voiding are preserved for the audit trail. For details on status transitions, see [Invoice Lifecycle](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/standalone-invoice-payment-lifecycle).
   * @param - Invoice ID.
   */
  (invoiceId: string): Promise<
    NonNullablePaths<
      VoidInvoiceResponse,
      | `invoice.status`
      | `invoice.statusQualifiers`
      | `invoice.availableActions`
      | `invoice.sourceReference.appId`
      | `invoice.reference.standaloneReference.limitActions`
      | `invoice.reference.orderReference.orderId`
      | `invoice.reference.migratedReference.limitActions`
      | `invoice.reference.autoChargeReference.limitActions`
      | `invoice.reference.referenceType`
      | `invoice.regionalProperties.locale.languageCode`
      | `invoice.regionalProperties.language`
      | `invoice.numbering.displayNumber`
      | `invoice.currency`
      | `invoice.customerInfo.contactDetails.vatId._id`
      | `invoice.customerInfo.contactDetails.vatId.type`
      | `invoice.customerInfo.billingAddress.streetAddress.number`
      | `invoice.customerInfo.billingAddress.streetAddress.name`
      | `invoice.lineItems`
      | `invoice.lineItems.${number}.customItem.quantity`
      | `invoice.lineItems.${number}.customItem.price`
      | `invoice.lineItems.${number}.customItem.catalogReference.catalogItemId`
      | `invoice.lineItems.${number}.customItem.catalogReference.appId`
      | `invoice.lineItems.${number}.catalogItem.quantity`
      | `invoice.lineItems.${number}.catalogItem.itemType.preset`
      | `invoice.lineItems.${number}.catalogItem.itemType.custom`
      | `invoice.lineItems.${number}.lineItemType`
      | `invoice.shipmentInfo.logistics.pickupDetails.pickupMethod`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.name`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
      | `invoice.shipmentInfo.totals.priceBeforeTax`
      | `invoice.shipmentInfo.totals.priceAfterTax`
      | `invoice.additionalFees`
      | `invoice.additionalFees.${number}.name`
      | `invoice.additionalFees.${number}.amount`
      | `invoice.discounts`
      | `invoice.discounts.${number}.fixedDiscount`
      | `invoice.discounts.${number}.percentageDiscount`
      | `invoice.discounts.${number}.discountType`
      | `invoice.discounts.${number}.appliedAmount`
      | `invoice.deposit.fixedDeposit`
      | `invoice.deposit.percentageDeposit`
      | `invoice.deposit.depositType`
      | `invoice.taxInfo.taxBreakdown`
      | `invoice.taxInfo.taxBreakdown.${number}.name`
      | `invoice.taxInfo.taxBreakdown.${number}.rate`
      | `invoice.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `invoice.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `invoice.taxInfo.taxBreakdown.${number}.taxAmount`
      | `invoice.taxInfo.taxExempt`
      | `invoice.payments`
      | `invoice.payments.${number}.regularPayment.paymentOrderId`
      | `invoice.payments.${number}.regularPayment.paymentMethod`
      | `invoice.payments.${number}.regularPayment.offlinePayment`
      | `invoice.payments.${number}.regularPayment.paymentStatus`
      | `invoice.payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
      | `invoice.payments.${number}.regularPayment.creditCardDetails.brand`
      | `invoice.payments.${number}.giftCardPayment.giftCardPaymentId`
      | `invoice.payments.${number}.giftCardPayment.appId`
      | `invoice.payments.${number}._id`
      | `invoice.payments.${number}.amount`
      | `invoice.payments.${number}.paymentType`
      | `invoice.payments.${number}.receiptType`
      | `invoice.payments.${number}.cashRounding.roundingAmount`
      | `invoice.payments.${number}.cashRounding.unroundedAmount`
      | `invoice.totals.subtotal`
      | `invoice.totals.total`
      | `invoice.totals.paidAmount`
      | `invoice.totals.balance`
      | `invoice.archived`
      | `invoice.customFields.headerCustomFields`
      | `invoice.customFields.headerCustomFields.${number}.value`
      | `invoice.customFields.businessCustomFields`
      | `invoice.customFields.customerCustomFields`
      | `invoice.customFields.footerCustomField.overridePresetContent`
      | `invoice.presetProperties.invoicePreset.name`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
      | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
      | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
      | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
      | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
      | `invoice.presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
      | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
      | `invoice.presetProperties.invoicePreset.customFields.businessCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.customerCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders`
      | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
      | `invoice.presetProperties.invoicePreset.customFields.customerPlaceholders`
      | `invoice.presetProperties.invoicePreset.defaultPreset`
      | `invoice.attachments`
      | `invoice.attachments.${number}.fileName`
      | `invoice.attachments.${number}.fileUrl`
      | `invoice.documentInfo.status`
      | `invoice.links.relativePath`
      | `invoice.businessLocation._id`
      | `invoice.activityInfo.sentToContactIds`
      | `invoice.hasSubscriptions`
      | `invoice.tags.privateTags.tagIds`,
      8
    > & {
      __applicationErrorsType?: VoidInvoiceApplicationErrors;
    }
  >;
}

export function archiveInvoice(
  httpClient: HttpClient
): ArchiveInvoiceSignature {
  return (invoiceId: string) =>
    universalArchiveInvoice(
      invoiceId,
      // @ts-ignore
      { httpClient }
    );
}

interface ArchiveInvoiceSignature {
  /**
   * Archives an invoice.
   *
   *
   * Use archiving to filter and organize invoices. Archiving is available for all statuses except `DRAFT`.
   *
   * To remove an invoice from the archive, call [Unarchive Invoice](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/unarchive-invoice).
   * @param - Invoice ID.
   */
  (invoiceId: string): Promise<
    NonNullablePaths<
      ArchiveInvoiceResponse,
      | `invoice.status`
      | `invoice.statusQualifiers`
      | `invoice.availableActions`
      | `invoice.sourceReference.appId`
      | `invoice.reference.standaloneReference.limitActions`
      | `invoice.reference.orderReference.orderId`
      | `invoice.reference.migratedReference.limitActions`
      | `invoice.reference.autoChargeReference.limitActions`
      | `invoice.reference.referenceType`
      | `invoice.regionalProperties.locale.languageCode`
      | `invoice.regionalProperties.language`
      | `invoice.numbering.displayNumber`
      | `invoice.currency`
      | `invoice.customerInfo.contactDetails.vatId._id`
      | `invoice.customerInfo.contactDetails.vatId.type`
      | `invoice.customerInfo.billingAddress.streetAddress.number`
      | `invoice.customerInfo.billingAddress.streetAddress.name`
      | `invoice.lineItems`
      | `invoice.lineItems.${number}.customItem.quantity`
      | `invoice.lineItems.${number}.customItem.price`
      | `invoice.lineItems.${number}.customItem.catalogReference.catalogItemId`
      | `invoice.lineItems.${number}.customItem.catalogReference.appId`
      | `invoice.lineItems.${number}.catalogItem.quantity`
      | `invoice.lineItems.${number}.catalogItem.itemType.preset`
      | `invoice.lineItems.${number}.catalogItem.itemType.custom`
      | `invoice.lineItems.${number}.lineItemType`
      | `invoice.shipmentInfo.logistics.pickupDetails.pickupMethod`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.name`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
      | `invoice.shipmentInfo.totals.priceBeforeTax`
      | `invoice.shipmentInfo.totals.priceAfterTax`
      | `invoice.additionalFees`
      | `invoice.additionalFees.${number}.name`
      | `invoice.additionalFees.${number}.amount`
      | `invoice.discounts`
      | `invoice.discounts.${number}.fixedDiscount`
      | `invoice.discounts.${number}.percentageDiscount`
      | `invoice.discounts.${number}.discountType`
      | `invoice.discounts.${number}.appliedAmount`
      | `invoice.deposit.fixedDeposit`
      | `invoice.deposit.percentageDeposit`
      | `invoice.deposit.depositType`
      | `invoice.taxInfo.taxBreakdown`
      | `invoice.taxInfo.taxBreakdown.${number}.name`
      | `invoice.taxInfo.taxBreakdown.${number}.rate`
      | `invoice.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `invoice.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `invoice.taxInfo.taxBreakdown.${number}.taxAmount`
      | `invoice.taxInfo.taxExempt`
      | `invoice.payments`
      | `invoice.payments.${number}.regularPayment.paymentOrderId`
      | `invoice.payments.${number}.regularPayment.paymentMethod`
      | `invoice.payments.${number}.regularPayment.offlinePayment`
      | `invoice.payments.${number}.regularPayment.paymentStatus`
      | `invoice.payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
      | `invoice.payments.${number}.regularPayment.creditCardDetails.brand`
      | `invoice.payments.${number}.giftCardPayment.giftCardPaymentId`
      | `invoice.payments.${number}.giftCardPayment.appId`
      | `invoice.payments.${number}._id`
      | `invoice.payments.${number}.amount`
      | `invoice.payments.${number}.paymentType`
      | `invoice.payments.${number}.receiptType`
      | `invoice.payments.${number}.cashRounding.roundingAmount`
      | `invoice.payments.${number}.cashRounding.unroundedAmount`
      | `invoice.totals.subtotal`
      | `invoice.totals.total`
      | `invoice.totals.paidAmount`
      | `invoice.totals.balance`
      | `invoice.archived`
      | `invoice.customFields.headerCustomFields`
      | `invoice.customFields.headerCustomFields.${number}.value`
      | `invoice.customFields.businessCustomFields`
      | `invoice.customFields.customerCustomFields`
      | `invoice.customFields.footerCustomField.overridePresetContent`
      | `invoice.presetProperties.invoicePreset.name`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
      | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
      | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
      | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
      | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
      | `invoice.presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
      | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
      | `invoice.presetProperties.invoicePreset.customFields.businessCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.customerCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders`
      | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
      | `invoice.presetProperties.invoicePreset.customFields.customerPlaceholders`
      | `invoice.presetProperties.invoicePreset.defaultPreset`
      | `invoice.attachments`
      | `invoice.attachments.${number}.fileName`
      | `invoice.attachments.${number}.fileUrl`
      | `invoice.documentInfo.status`
      | `invoice.links.relativePath`
      | `invoice.businessLocation._id`
      | `invoice.activityInfo.sentToContactIds`
      | `invoice.hasSubscriptions`
      | `invoice.tags.privateTags.tagIds`,
      8
    > & {
      __applicationErrorsType?: ArchiveInvoiceApplicationErrors;
    }
  >;
}

export function unarchiveInvoice(
  httpClient: HttpClient
): UnarchiveInvoiceSignature {
  return (invoiceId: string) =>
    universalUnarchiveInvoice(
      invoiceId,
      // @ts-ignore
      { httpClient }
    );
}

interface UnarchiveInvoiceSignature {
  /**
   * Unarchives an invoice.
   *
   *
   * Removes the invoice from the archived state. To archive an invoice, call [Archive Invoice](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/archive-invoice).
   * @param - Invoice ID.
   */
  (invoiceId: string): Promise<
    NonNullablePaths<
      UnarchiveInvoiceResponse,
      | `invoice.status`
      | `invoice.statusQualifiers`
      | `invoice.availableActions`
      | `invoice.sourceReference.appId`
      | `invoice.reference.standaloneReference.limitActions`
      | `invoice.reference.orderReference.orderId`
      | `invoice.reference.migratedReference.limitActions`
      | `invoice.reference.autoChargeReference.limitActions`
      | `invoice.reference.referenceType`
      | `invoice.regionalProperties.locale.languageCode`
      | `invoice.regionalProperties.language`
      | `invoice.numbering.displayNumber`
      | `invoice.currency`
      | `invoice.customerInfo.contactDetails.vatId._id`
      | `invoice.customerInfo.contactDetails.vatId.type`
      | `invoice.customerInfo.billingAddress.streetAddress.number`
      | `invoice.customerInfo.billingAddress.streetAddress.name`
      | `invoice.lineItems`
      | `invoice.lineItems.${number}.customItem.quantity`
      | `invoice.lineItems.${number}.customItem.price`
      | `invoice.lineItems.${number}.customItem.catalogReference.catalogItemId`
      | `invoice.lineItems.${number}.customItem.catalogReference.appId`
      | `invoice.lineItems.${number}.catalogItem.quantity`
      | `invoice.lineItems.${number}.catalogItem.itemType.preset`
      | `invoice.lineItems.${number}.catalogItem.itemType.custom`
      | `invoice.lineItems.${number}.lineItemType`
      | `invoice.shipmentInfo.logistics.pickupDetails.pickupMethod`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.name`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
      | `invoice.shipmentInfo.totals.priceBeforeTax`
      | `invoice.shipmentInfo.totals.priceAfterTax`
      | `invoice.additionalFees`
      | `invoice.additionalFees.${number}.name`
      | `invoice.additionalFees.${number}.amount`
      | `invoice.discounts`
      | `invoice.discounts.${number}.fixedDiscount`
      | `invoice.discounts.${number}.percentageDiscount`
      | `invoice.discounts.${number}.discountType`
      | `invoice.discounts.${number}.appliedAmount`
      | `invoice.deposit.fixedDeposit`
      | `invoice.deposit.percentageDeposit`
      | `invoice.deposit.depositType`
      | `invoice.taxInfo.taxBreakdown`
      | `invoice.taxInfo.taxBreakdown.${number}.name`
      | `invoice.taxInfo.taxBreakdown.${number}.rate`
      | `invoice.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `invoice.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `invoice.taxInfo.taxBreakdown.${number}.taxAmount`
      | `invoice.taxInfo.taxExempt`
      | `invoice.payments`
      | `invoice.payments.${number}.regularPayment.paymentOrderId`
      | `invoice.payments.${number}.regularPayment.paymentMethod`
      | `invoice.payments.${number}.regularPayment.offlinePayment`
      | `invoice.payments.${number}.regularPayment.paymentStatus`
      | `invoice.payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
      | `invoice.payments.${number}.regularPayment.creditCardDetails.brand`
      | `invoice.payments.${number}.giftCardPayment.giftCardPaymentId`
      | `invoice.payments.${number}.giftCardPayment.appId`
      | `invoice.payments.${number}._id`
      | `invoice.payments.${number}.amount`
      | `invoice.payments.${number}.paymentType`
      | `invoice.payments.${number}.receiptType`
      | `invoice.payments.${number}.cashRounding.roundingAmount`
      | `invoice.payments.${number}.cashRounding.unroundedAmount`
      | `invoice.totals.subtotal`
      | `invoice.totals.total`
      | `invoice.totals.paidAmount`
      | `invoice.totals.balance`
      | `invoice.archived`
      | `invoice.customFields.headerCustomFields`
      | `invoice.customFields.headerCustomFields.${number}.value`
      | `invoice.customFields.businessCustomFields`
      | `invoice.customFields.customerCustomFields`
      | `invoice.customFields.footerCustomField.overridePresetContent`
      | `invoice.presetProperties.invoicePreset.name`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
      | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
      | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
      | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
      | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
      | `invoice.presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
      | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
      | `invoice.presetProperties.invoicePreset.customFields.businessCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.customerCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders`
      | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
      | `invoice.presetProperties.invoicePreset.customFields.customerPlaceholders`
      | `invoice.presetProperties.invoicePreset.defaultPreset`
      | `invoice.attachments`
      | `invoice.attachments.${number}.fileName`
      | `invoice.attachments.${number}.fileUrl`
      | `invoice.documentInfo.status`
      | `invoice.links.relativePath`
      | `invoice.businessLocation._id`
      | `invoice.activityInfo.sentToContactIds`
      | `invoice.hasSubscriptions`
      | `invoice.tags.privateTags.tagIds`,
      8
    > & {
      __applicationErrorsType?: UnarchiveInvoiceApplicationErrors;
    }
  >;
}

export function initiatePayment(
  httpClient: HttpClient
): InitiatePaymentSignature {
  return (invoiceId: string) =>
    universalInitiatePayment(
      invoiceId,
      // @ts-ignore
      { httpClient }
    );
}

interface InitiatePaymentSignature {
  /**
   * Initiates payment for an invoice.
   *
   *
   * Creates a Wix eCommerce order if one doesn't exist, generates a payment request, and returns a checkout URL for the customer.
   * @param - Invoice ID.
   */
  (invoiceId: string): Promise<
    NonNullablePaths<InitiatePaymentResponse, `url` | `paymentRequestId`, 2> & {
      __applicationErrorsType?: InitiatePaymentApplicationErrors;
    }
  >;
}

export function enableInvoicePayments(
  httpClient: HttpClient
): EnableInvoicePaymentsSignature {
  return (invoiceId: string) =>
    universalEnableInvoicePayments(
      invoiceId,
      // @ts-ignore
      { httpClient }
    );
}

interface EnableInvoicePaymentsSignature {
  /**
   * Enables payments for an invoice.
   *
   *
   * Use this to record manual (offline) or MOTO (Mail Order/Telephone Order) payments against an invoice. Creates a Wix eCommerce order if one doesn't exist, which enables payment recording against the invoice. To generate a checkout URL for online payment instead, call [Initiate Payment](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/initiate-payment).
   * @param - Invoice ID.
   */
  (invoiceId: string): Promise<
    NonNullablePaths<EnableInvoicePaymentsResponse, `orderId`, 2> & {
      __applicationErrorsType?: EnableInvoicePaymentsApplicationErrors;
    }
  >;
}

export function generatePdfDocument(
  httpClient: HttpClient
): GeneratePdfDocumentSignature {
  return (invoiceId: string) =>
    universalGeneratePdfDocument(
      invoiceId,
      // @ts-ignore
      { httpClient }
    );
}

interface GeneratePdfDocumentSignature {
  /**
   * Generates a PDF document for an invoice.
   *
   *
   * PDF generation is asynchronous. Poll the invoice using [Get Invoice](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/get-invoice) and check `documentInfo.status` until it's `AVAILABLE`, then use `documentInfo.downloadUrl` to access the file.
   * @param - Invoice ID.
   */
  (invoiceId: string): Promise<
    NonNullablePaths<
      GeneratePdfDocumentResponse,
      | `invoice.status`
      | `invoice.statusQualifiers`
      | `invoice.availableActions`
      | `invoice.sourceReference.appId`
      | `invoice.reference.standaloneReference.limitActions`
      | `invoice.reference.orderReference.orderId`
      | `invoice.reference.migratedReference.limitActions`
      | `invoice.reference.autoChargeReference.limitActions`
      | `invoice.reference.referenceType`
      | `invoice.regionalProperties.locale.languageCode`
      | `invoice.regionalProperties.language`
      | `invoice.numbering.displayNumber`
      | `invoice.currency`
      | `invoice.customerInfo.contactDetails.vatId._id`
      | `invoice.customerInfo.contactDetails.vatId.type`
      | `invoice.customerInfo.billingAddress.streetAddress.number`
      | `invoice.customerInfo.billingAddress.streetAddress.name`
      | `invoice.lineItems`
      | `invoice.lineItems.${number}.customItem.quantity`
      | `invoice.lineItems.${number}.customItem.price`
      | `invoice.lineItems.${number}.customItem.catalogReference.catalogItemId`
      | `invoice.lineItems.${number}.customItem.catalogReference.appId`
      | `invoice.lineItems.${number}.catalogItem.quantity`
      | `invoice.lineItems.${number}.catalogItem.itemType.preset`
      | `invoice.lineItems.${number}.catalogItem.itemType.custom`
      | `invoice.lineItems.${number}.lineItemType`
      | `invoice.shipmentInfo.logistics.pickupDetails.pickupMethod`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.name`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
      | `invoice.shipmentInfo.totals.priceBeforeTax`
      | `invoice.shipmentInfo.totals.priceAfterTax`
      | `invoice.additionalFees`
      | `invoice.additionalFees.${number}.name`
      | `invoice.additionalFees.${number}.amount`
      | `invoice.discounts`
      | `invoice.discounts.${number}.fixedDiscount`
      | `invoice.discounts.${number}.percentageDiscount`
      | `invoice.discounts.${number}.discountType`
      | `invoice.discounts.${number}.appliedAmount`
      | `invoice.deposit.fixedDeposit`
      | `invoice.deposit.percentageDeposit`
      | `invoice.deposit.depositType`
      | `invoice.taxInfo.taxBreakdown`
      | `invoice.taxInfo.taxBreakdown.${number}.name`
      | `invoice.taxInfo.taxBreakdown.${number}.rate`
      | `invoice.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `invoice.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `invoice.taxInfo.taxBreakdown.${number}.taxAmount`
      | `invoice.taxInfo.taxExempt`
      | `invoice.payments`
      | `invoice.payments.${number}.regularPayment.paymentOrderId`
      | `invoice.payments.${number}.regularPayment.paymentMethod`
      | `invoice.payments.${number}.regularPayment.offlinePayment`
      | `invoice.payments.${number}.regularPayment.paymentStatus`
      | `invoice.payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
      | `invoice.payments.${number}.regularPayment.creditCardDetails.brand`
      | `invoice.payments.${number}.giftCardPayment.giftCardPaymentId`
      | `invoice.payments.${number}.giftCardPayment.appId`
      | `invoice.payments.${number}._id`
      | `invoice.payments.${number}.amount`
      | `invoice.payments.${number}.paymentType`
      | `invoice.payments.${number}.receiptType`
      | `invoice.payments.${number}.cashRounding.roundingAmount`
      | `invoice.payments.${number}.cashRounding.unroundedAmount`
      | `invoice.totals.subtotal`
      | `invoice.totals.total`
      | `invoice.totals.paidAmount`
      | `invoice.totals.balance`
      | `invoice.archived`
      | `invoice.customFields.headerCustomFields`
      | `invoice.customFields.headerCustomFields.${number}.value`
      | `invoice.customFields.businessCustomFields`
      | `invoice.customFields.customerCustomFields`
      | `invoice.customFields.footerCustomField.overridePresetContent`
      | `invoice.presetProperties.invoicePreset.name`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
      | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
      | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
      | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
      | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
      | `invoice.presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
      | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
      | `invoice.presetProperties.invoicePreset.customFields.businessCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.customerCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders`
      | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
      | `invoice.presetProperties.invoicePreset.customFields.customerPlaceholders`
      | `invoice.presetProperties.invoicePreset.defaultPreset`
      | `invoice.attachments`
      | `invoice.attachments.${number}.fileName`
      | `invoice.attachments.${number}.fileUrl`
      | `invoice.documentInfo.status`
      | `invoice.links.relativePath`
      | `invoice.businessLocation._id`
      | `invoice.activityInfo.sentToContactIds`
      | `invoice.hasSubscriptions`
      | `invoice.tags.privateTags.tagIds`,
      8
    > & {
      __applicationErrorsType?: GeneratePdfDocumentApplicationErrors;
    }
  >;
}

export function markInvoiceAsViewed(
  httpClient: HttpClient
): MarkInvoiceAsViewedSignature {
  return (invoiceId: string) =>
    universalMarkInvoiceAsViewed(
      invoiceId,
      // @ts-ignore
      { httpClient }
    );
}

interface MarkInvoiceAsViewedSignature {
  /**
   * Marks an invoice as viewed.
   *
   *
   * Adds the `VIEWED` status qualifier to the invoice and records the view timestamp in `activityInfo`. For details, see [Status Qualifiers](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/status-qualifiers).
   * @param - Invoice ID.
   */
  (invoiceId: string): Promise<
    NonNullablePaths<
      MarkInvoiceAsViewedResponse,
      | `invoice.status`
      | `invoice.statusQualifiers`
      | `invoice.availableActions`
      | `invoice.sourceReference.appId`
      | `invoice.reference.standaloneReference.limitActions`
      | `invoice.reference.orderReference.orderId`
      | `invoice.reference.migratedReference.limitActions`
      | `invoice.reference.autoChargeReference.limitActions`
      | `invoice.reference.referenceType`
      | `invoice.regionalProperties.locale.languageCode`
      | `invoice.regionalProperties.language`
      | `invoice.numbering.displayNumber`
      | `invoice.currency`
      | `invoice.customerInfo.contactDetails.vatId._id`
      | `invoice.customerInfo.contactDetails.vatId.type`
      | `invoice.customerInfo.billingAddress.streetAddress.number`
      | `invoice.customerInfo.billingAddress.streetAddress.name`
      | `invoice.lineItems`
      | `invoice.lineItems.${number}.customItem.quantity`
      | `invoice.lineItems.${number}.customItem.price`
      | `invoice.lineItems.${number}.customItem.catalogReference.catalogItemId`
      | `invoice.lineItems.${number}.customItem.catalogReference.appId`
      | `invoice.lineItems.${number}.catalogItem.quantity`
      | `invoice.lineItems.${number}.catalogItem.itemType.preset`
      | `invoice.lineItems.${number}.catalogItem.itemType.custom`
      | `invoice.lineItems.${number}.lineItemType`
      | `invoice.shipmentInfo.logistics.pickupDetails.pickupMethod`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.name`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
      | `invoice.shipmentInfo.totals.priceBeforeTax`
      | `invoice.shipmentInfo.totals.priceAfterTax`
      | `invoice.additionalFees`
      | `invoice.additionalFees.${number}.name`
      | `invoice.additionalFees.${number}.amount`
      | `invoice.discounts`
      | `invoice.discounts.${number}.fixedDiscount`
      | `invoice.discounts.${number}.percentageDiscount`
      | `invoice.discounts.${number}.discountType`
      | `invoice.discounts.${number}.appliedAmount`
      | `invoice.deposit.fixedDeposit`
      | `invoice.deposit.percentageDeposit`
      | `invoice.deposit.depositType`
      | `invoice.taxInfo.taxBreakdown`
      | `invoice.taxInfo.taxBreakdown.${number}.name`
      | `invoice.taxInfo.taxBreakdown.${number}.rate`
      | `invoice.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `invoice.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `invoice.taxInfo.taxBreakdown.${number}.taxAmount`
      | `invoice.taxInfo.taxExempt`
      | `invoice.payments`
      | `invoice.payments.${number}.regularPayment.paymentOrderId`
      | `invoice.payments.${number}.regularPayment.paymentMethod`
      | `invoice.payments.${number}.regularPayment.offlinePayment`
      | `invoice.payments.${number}.regularPayment.paymentStatus`
      | `invoice.payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
      | `invoice.payments.${number}.regularPayment.creditCardDetails.brand`
      | `invoice.payments.${number}.giftCardPayment.giftCardPaymentId`
      | `invoice.payments.${number}.giftCardPayment.appId`
      | `invoice.payments.${number}._id`
      | `invoice.payments.${number}.amount`
      | `invoice.payments.${number}.paymentType`
      | `invoice.payments.${number}.receiptType`
      | `invoice.payments.${number}.cashRounding.roundingAmount`
      | `invoice.payments.${number}.cashRounding.unroundedAmount`
      | `invoice.totals.subtotal`
      | `invoice.totals.total`
      | `invoice.totals.paidAmount`
      | `invoice.totals.balance`
      | `invoice.archived`
      | `invoice.customFields.headerCustomFields`
      | `invoice.customFields.headerCustomFields.${number}.value`
      | `invoice.customFields.businessCustomFields`
      | `invoice.customFields.customerCustomFields`
      | `invoice.customFields.footerCustomField.overridePresetContent`
      | `invoice.presetProperties.invoicePreset.name`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
      | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
      | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
      | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
      | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
      | `invoice.presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
      | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
      | `invoice.presetProperties.invoicePreset.customFields.businessCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.customerCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders`
      | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
      | `invoice.presetProperties.invoicePreset.customFields.customerPlaceholders`
      | `invoice.presetProperties.invoicePreset.defaultPreset`
      | `invoice.attachments`
      | `invoice.attachments.${number}.fileName`
      | `invoice.attachments.${number}.fileUrl`
      | `invoice.documentInfo.status`
      | `invoice.links.relativePath`
      | `invoice.businessLocation._id`
      | `invoice.activityInfo.sentToContactIds`
      | `invoice.hasSubscriptions`
      | `invoice.tags.privateTags.tagIds`,
      8
    >
  >;
}

export function markInvoiceAsSent(
  httpClient: HttpClient
): MarkInvoiceAsSentSignature {
  return (invoiceId: string) =>
    universalMarkInvoiceAsSent(
      invoiceId,
      // @ts-ignore
      { httpClient }
    );
}

interface MarkInvoiceAsSentSignature {
  /**
   * Marks an invoice as sent.
   *
   *
   * Adds the `SENT` status qualifier to the invoice and records the send timestamp and recipient in `activityInfo`. For details, see [Status Qualifiers](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/status-qualifiers).
   * @param - Invoice ID.
   */
  (invoiceId: string): Promise<
    NonNullablePaths<
      MarkInvoiceAsSentResponse,
      | `invoice.status`
      | `invoice.statusQualifiers`
      | `invoice.availableActions`
      | `invoice.sourceReference.appId`
      | `invoice.reference.standaloneReference.limitActions`
      | `invoice.reference.orderReference.orderId`
      | `invoice.reference.migratedReference.limitActions`
      | `invoice.reference.autoChargeReference.limitActions`
      | `invoice.reference.referenceType`
      | `invoice.regionalProperties.locale.languageCode`
      | `invoice.regionalProperties.language`
      | `invoice.numbering.displayNumber`
      | `invoice.currency`
      | `invoice.customerInfo.contactDetails.vatId._id`
      | `invoice.customerInfo.contactDetails.vatId.type`
      | `invoice.customerInfo.billingAddress.streetAddress.number`
      | `invoice.customerInfo.billingAddress.streetAddress.name`
      | `invoice.lineItems`
      | `invoice.lineItems.${number}.customItem.quantity`
      | `invoice.lineItems.${number}.customItem.price`
      | `invoice.lineItems.${number}.customItem.catalogReference.catalogItemId`
      | `invoice.lineItems.${number}.customItem.catalogReference.appId`
      | `invoice.lineItems.${number}.catalogItem.quantity`
      | `invoice.lineItems.${number}.catalogItem.itemType.preset`
      | `invoice.lineItems.${number}.catalogItem.itemType.custom`
      | `invoice.lineItems.${number}.lineItemType`
      | `invoice.shipmentInfo.logistics.pickupDetails.pickupMethod`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.name`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
      | `invoice.shipmentInfo.totals.priceBeforeTax`
      | `invoice.shipmentInfo.totals.priceAfterTax`
      | `invoice.additionalFees`
      | `invoice.additionalFees.${number}.name`
      | `invoice.additionalFees.${number}.amount`
      | `invoice.discounts`
      | `invoice.discounts.${number}.fixedDiscount`
      | `invoice.discounts.${number}.percentageDiscount`
      | `invoice.discounts.${number}.discountType`
      | `invoice.discounts.${number}.appliedAmount`
      | `invoice.deposit.fixedDeposit`
      | `invoice.deposit.percentageDeposit`
      | `invoice.deposit.depositType`
      | `invoice.taxInfo.taxBreakdown`
      | `invoice.taxInfo.taxBreakdown.${number}.name`
      | `invoice.taxInfo.taxBreakdown.${number}.rate`
      | `invoice.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `invoice.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `invoice.taxInfo.taxBreakdown.${number}.taxAmount`
      | `invoice.taxInfo.taxExempt`
      | `invoice.payments`
      | `invoice.payments.${number}.regularPayment.paymentOrderId`
      | `invoice.payments.${number}.regularPayment.paymentMethod`
      | `invoice.payments.${number}.regularPayment.offlinePayment`
      | `invoice.payments.${number}.regularPayment.paymentStatus`
      | `invoice.payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
      | `invoice.payments.${number}.regularPayment.creditCardDetails.brand`
      | `invoice.payments.${number}.giftCardPayment.giftCardPaymentId`
      | `invoice.payments.${number}.giftCardPayment.appId`
      | `invoice.payments.${number}._id`
      | `invoice.payments.${number}.amount`
      | `invoice.payments.${number}.paymentType`
      | `invoice.payments.${number}.receiptType`
      | `invoice.payments.${number}.cashRounding.roundingAmount`
      | `invoice.payments.${number}.cashRounding.unroundedAmount`
      | `invoice.totals.subtotal`
      | `invoice.totals.total`
      | `invoice.totals.paidAmount`
      | `invoice.totals.balance`
      | `invoice.archived`
      | `invoice.customFields.headerCustomFields`
      | `invoice.customFields.headerCustomFields.${number}.value`
      | `invoice.customFields.businessCustomFields`
      | `invoice.customFields.customerCustomFields`
      | `invoice.customFields.footerCustomField.overridePresetContent`
      | `invoice.presetProperties.invoicePreset.name`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
      | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
      | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
      | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
      | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
      | `invoice.presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
      | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
      | `invoice.presetProperties.invoicePreset.customFields.businessCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.customerCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders`
      | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
      | `invoice.presetProperties.invoicePreset.customFields.customerPlaceholders`
      | `invoice.presetProperties.invoicePreset.defaultPreset`
      | `invoice.attachments`
      | `invoice.attachments.${number}.fileName`
      | `invoice.attachments.${number}.fileUrl`
      | `invoice.documentInfo.status`
      | `invoice.links.relativePath`
      | `invoice.businessLocation._id`
      | `invoice.activityInfo.sentToContactIds`
      | `invoice.hasSubscriptions`
      | `invoice.tags.privateTags.tagIds`,
      8
    >
  >;
}

export function calculateInvoice(
  httpClient: HttpClient
): CalculateInvoiceSignature {
  return (
    invoice: NonNullablePaths<
      Invoice,
      `reference` | `reference.referenceType`,
      3
    >,
    options?: CalculateInvoiceOptions
  ) =>
    universalCalculateInvoice(
      invoice,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CalculateInvoiceSignature {
  /**
   * Calculates totals, taxes, and available shipping options for a standalone invoice. Supports both custom and catalog line items.
   *
   *
   * Use this method before creating or updating an invoice to preview calculated values. Pass the returned `correlationId` in subsequent calculations to optimize incremental recalculations.
   * @param - Invoice to calculate.
   */
  (
    invoice: NonNullablePaths<
      Invoice,
      `reference` | `reference.referenceType`,
      3
    >,
    options?: CalculateInvoiceOptions
  ): Promise<
    NonNullablePaths<
      CalculateInvoiceResponse,
      | `invoice.status`
      | `invoice.statusQualifiers`
      | `invoice.availableActions`
      | `invoice.sourceReference.appId`
      | `invoice.reference.standaloneReference.limitActions`
      | `invoice.reference.orderReference.orderId`
      | `invoice.reference.migratedReference.limitActions`
      | `invoice.reference.autoChargeReference.limitActions`
      | `invoice.reference.referenceType`
      | `invoice.regionalProperties.locale.languageCode`
      | `invoice.regionalProperties.language`
      | `invoice.numbering.displayNumber`
      | `invoice.currency`
      | `invoice.customerInfo.contactDetails.vatId._id`
      | `invoice.customerInfo.contactDetails.vatId.type`
      | `invoice.customerInfo.billingAddress.streetAddress.number`
      | `invoice.customerInfo.billingAddress.streetAddress.name`
      | `invoice.lineItems`
      | `invoice.lineItems.${number}.customItem.quantity`
      | `invoice.lineItems.${number}.customItem.price`
      | `invoice.lineItems.${number}.customItem.catalogReference.catalogItemId`
      | `invoice.lineItems.${number}.customItem.catalogReference.appId`
      | `invoice.lineItems.${number}.catalogItem.quantity`
      | `invoice.lineItems.${number}.catalogItem.itemType.preset`
      | `invoice.lineItems.${number}.catalogItem.itemType.custom`
      | `invoice.lineItems.${number}.lineItemType`
      | `invoice.shipmentInfo.logistics.pickupDetails.pickupMethod`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.name`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.rate`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `invoice.shipmentInfo.taxInfo.taxBreakdown.${number}.taxAmount`
      | `invoice.shipmentInfo.totals.priceBeforeTax`
      | `invoice.shipmentInfo.totals.priceAfterTax`
      | `invoice.additionalFees`
      | `invoice.additionalFees.${number}.name`
      | `invoice.additionalFees.${number}.amount`
      | `invoice.discounts`
      | `invoice.discounts.${number}.fixedDiscount`
      | `invoice.discounts.${number}.percentageDiscount`
      | `invoice.discounts.${number}.discountType`
      | `invoice.discounts.${number}.appliedAmount`
      | `invoice.deposit.fixedDeposit`
      | `invoice.deposit.percentageDeposit`
      | `invoice.deposit.depositType`
      | `invoice.taxInfo.taxBreakdown`
      | `invoice.taxInfo.taxBreakdown.${number}.name`
      | `invoice.taxInfo.taxBreakdown.${number}.rate`
      | `invoice.taxInfo.taxBreakdown.${number}.taxableAmount`
      | `invoice.taxInfo.taxBreakdown.${number}.exemptAmount`
      | `invoice.taxInfo.taxBreakdown.${number}.taxAmount`
      | `invoice.taxInfo.taxExempt`
      | `invoice.payments`
      | `invoice.payments.${number}.regularPayment.paymentOrderId`
      | `invoice.payments.${number}.regularPayment.paymentMethod`
      | `invoice.payments.${number}.regularPayment.offlinePayment`
      | `invoice.payments.${number}.regularPayment.paymentStatus`
      | `invoice.payments.${number}.regularPayment.creditCardDetails.lastFourDigits`
      | `invoice.payments.${number}.regularPayment.creditCardDetails.brand`
      | `invoice.payments.${number}.giftCardPayment.giftCardPaymentId`
      | `invoice.payments.${number}.giftCardPayment.appId`
      | `invoice.payments.${number}._id`
      | `invoice.payments.${number}.amount`
      | `invoice.payments.${number}.paymentType`
      | `invoice.payments.${number}.receiptType`
      | `invoice.payments.${number}.cashRounding.roundingAmount`
      | `invoice.payments.${number}.cashRounding.unroundedAmount`
      | `invoice.totals.subtotal`
      | `invoice.totals.total`
      | `invoice.totals.paidAmount`
      | `invoice.totals.balance`
      | `invoice.archived`
      | `invoice.customFields.headerCustomFields`
      | `invoice.customFields.headerCustomFields.${number}.value`
      | `invoice.customFields.businessCustomFields`
      | `invoice.customFields.customerCustomFields`
      | `invoice.customFields.footerCustomField.overridePresetContent`
      | `invoice.presetProperties.invoicePreset.name`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayEmail`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayPhoneNumber`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.businessInfo.displayCompanyId`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayEmail`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayPhoneNumber`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayShippingAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayBillingAddress`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayVatId`
      | `invoice.presetProperties.invoicePreset.displaySettings.customerInfo.displayCompanyName`
      | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayDescription`
      | `invoice.presetProperties.invoicePreset.displaySettings.itemsInfo.displayTax`
      | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayTaxBreakdown`
      | `invoice.presetProperties.invoicePreset.displaySettings.totalsInfo.displayItemSubtotalPerTaxBreakdown`
      | `invoice.presetProperties.invoicePreset.displaySettings.paymentsInfo.displayPaymentsInfo`
      | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.headerCustomFields.${number}.value`
      | `invoice.presetProperties.invoicePreset.customFields.businessCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.customerCustomFields`
      | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders`
      | `invoice.presetProperties.invoicePreset.customFields.headerPlaceholders.${number}.title`
      | `invoice.presetProperties.invoicePreset.customFields.customerPlaceholders`
      | `invoice.presetProperties.invoicePreset.defaultPreset`
      | `invoice.attachments`
      | `invoice.attachments.${number}.fileName`
      | `invoice.attachments.${number}.fileUrl`
      | `invoice.documentInfo.status`
      | `invoice.links.relativePath`
      | `invoice.businessLocation._id`
      | `invoice.activityInfo.sentToContactIds`
      | `invoice.hasSubscriptions`
      | `invoice.tags.privateTags.tagIds`
      | `shipmentOptions`
      | `shipmentOptions.${number}.logistics.pickupDetails.pickupMethod`
      | `calculationErrors.generalShippingCalculationError.applicationError.code`
      | `calculationErrors.generalShippingCalculationError.applicationError.description`
      | `calculationErrors.generalShippingCalculationError.validationError.fieldViolations`
      | `calculationErrors.generalShippingCalculationError.validationError.fieldViolations.${number}.field`
      | `calculationErrors.generalShippingCalculationError.validationError.fieldViolations.${number}.description`
      | `calculationErrors.generalShippingCalculationError.validationError.fieldViolations.${number}.violatedRule`
      | `calculationErrors.carrierErrors.errors`
      | `calculationErrors.carrierErrors.errors.${number}.carrierId`
      | `correlationId`,
      8
    > & {
      __applicationErrorsType?: CalculateInvoiceApplicationErrors;
    }
  >;
}

export function getLatestInvoiceNumber(
  httpClient: HttpClient
): GetLatestInvoiceNumberSignature {
  return (options?: GetLatestInvoiceNumberOptions) =>
    universalGetLatestInvoiceNumber(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetLatestInvoiceNumberSignature {
  /**
   * Retrieves the highest invoice number currently in use.
   *
   *
   * Invoice numbering is managed in the [Invoices Settings API](https://dev.wix.com/docs/rest/business-management/get-paid/invoices/invoices-settings/introduction).
   */
  (
    options?: GetLatestInvoiceNumberOptions
  ): Promise<GetLatestInvoiceNumberResponse>;
}

export function generateReceipt(
  httpClient: HttpClient
): GenerateReceiptSignature {
  return (invoiceId: string, paymentId: string) =>
    universalGenerateReceipt(
      invoiceId,
      paymentId,
      // @ts-ignore
      { httpClient }
    );
}

interface GenerateReceiptSignature {
  /**
   * Generates a receipt document for a specific payment on an invoice. Requires the `paymentId` of a confirmed payment on the invoice.
   * @param - Invoice ID.
   * @param - Payment ID.
   */
  (invoiceId: string, paymentId: string): Promise<
    NonNullablePaths<GenerateReceiptResponse, `receiptId`, 2>
  >;
}

export function bulkCreateInvoices(
  httpClient: HttpClient
): BulkCreateInvoicesSignature {
  return (
    invoices: NonNullablePaths<
      Invoice,
      | `currency`
      | `customerInfo`
      | `customerInfo.contactId`
      | `issueDate`
      | `lineItems`
      | `lineItems.${number}.catalogItem.catalogReference`
      | `lineItems.${number}.catalogItem.catalogReference.appId`
      | `lineItems.${number}.catalogItem.catalogReference.catalogItemId`
      | `lineItems.${number}.customItem.name`
      | `lineItems.${number}.customItem.price`
      | `lineItems.${number}.customItem.quantity`
      | `lineItems.${number}.lineItemType`
      | `reference`
      | `reference.orderReference.orderId`
      | `reference.referenceType`
      | `shipmentInfo.code`
      | `shipmentInfo.price`
      | `shippingInfo.contactDetails`
      | `shippingInfo.contactDetails.email`
      | `shippingInfo.shippingAddress`,
      6
    >[],
    options?: BulkCreateInvoicesOptions
  ) =>
    universalBulkCreateInvoices(
      invoices,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateInvoicesSignature {
  /**
   * Creates multiple invoices.
   *
   *
   * To create a single invoice, call [Create Invoice](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/create-invoice).
   * @param - Invoices to create.
   */
  (
    invoices: NonNullablePaths<
      Invoice,
      | `currency`
      | `customerInfo`
      | `customerInfo.contactId`
      | `issueDate`
      | `lineItems`
      | `lineItems.${number}.catalogItem.catalogReference`
      | `lineItems.${number}.catalogItem.catalogReference.appId`
      | `lineItems.${number}.catalogItem.catalogReference.catalogItemId`
      | `lineItems.${number}.customItem.name`
      | `lineItems.${number}.customItem.price`
      | `lineItems.${number}.customItem.quantity`
      | `lineItems.${number}.lineItemType`
      | `reference`
      | `reference.orderReference.orderId`
      | `reference.referenceType`
      | `shipmentInfo.code`
      | `shipmentInfo.price`
      | `shippingInfo.contactDetails`
      | `shippingInfo.contactDetails.email`
      | `shippingInfo.shippingAddress`,
      6
    >[],
    options?: BulkCreateInvoicesOptions
  ): Promise<
    NonNullablePaths<
      BulkCreateInvoicesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.status`
      | `results.${number}.item.sourceReference.appId`
      | `results.${number}.item.reference.referenceType`
      | `results.${number}.item.regionalProperties.language`
      | `results.${number}.item.numbering.displayNumber`
      | `results.${number}.item.currency`
      | `results.${number}.item.deposit.fixedDeposit`
      | `results.${number}.item.deposit.percentageDeposit`
      | `results.${number}.item.deposit.depositType`
      | `results.${number}.item.taxInfo.taxExempt`
      | `results.${number}.item.totals.subtotal`
      | `results.${number}.item.totals.total`
      | `results.${number}.item.totals.paidAmount`
      | `results.${number}.item.totals.balance`
      | `results.${number}.item.archived`
      | `results.${number}.item.documentInfo.status`
      | `results.${number}.item.links.relativePath`
      | `results.${number}.item.businessLocation._id`
      | `results.${number}.item.hasSubscriptions`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkCreateInvoicesApplicationErrors;
      __validationErrorsType?: BulkCreateInvoicesValidationErrors;
    }
  >;
}

export function bulkUpdateInvoices(
  httpClient: HttpClient
): BulkUpdateInvoicesSignature {
  return (
    invoices: NonNullablePaths<
      MaskedInvoice,
      | `invoice`
      | `invoice._id`
      | `invoice.lineItems.${number}.customItem.name`
      | `invoice.revision`,
      6
    >[],
    options?: BulkUpdateInvoicesOptions
  ) =>
    universalBulkUpdateInvoices(
      invoices,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateInvoicesSignature {
  /**
   * Updates multiple invoices.
   *
   *
   * To update a single invoice, call [Update Invoice](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/update-invoice).
   * @param - Invoices to update.
   */
  (
    invoices: NonNullablePaths<
      MaskedInvoice,
      | `invoice`
      | `invoice._id`
      | `invoice.lineItems.${number}.customItem.name`
      | `invoice.revision`,
      6
    >[],
    options?: BulkUpdateInvoicesOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateInvoicesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.status`
      | `results.${number}.item.sourceReference.appId`
      | `results.${number}.item.reference.referenceType`
      | `results.${number}.item.regionalProperties.language`
      | `results.${number}.item.numbering.displayNumber`
      | `results.${number}.item.currency`
      | `results.${number}.item.deposit.fixedDeposit`
      | `results.${number}.item.deposit.percentageDeposit`
      | `results.${number}.item.deposit.depositType`
      | `results.${number}.item.taxInfo.taxExempt`
      | `results.${number}.item.totals.subtotal`
      | `results.${number}.item.totals.total`
      | `results.${number}.item.totals.paidAmount`
      | `results.${number}.item.totals.balance`
      | `results.${number}.item.archived`
      | `results.${number}.item.documentInfo.status`
      | `results.${number}.item.links.relativePath`
      | `results.${number}.item.businessLocation._id`
      | `results.${number}.item.hasSubscriptions`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateInvoicesApplicationErrors;
      __validationErrorsType?: BulkUpdateInvoicesValidationErrors;
    }
  >;
}

export function bulkDeleteInvoices(
  httpClient: HttpClient
): BulkDeleteInvoicesSignature {
  return (invoiceIds: string[]) =>
    universalBulkDeleteInvoices(
      invoiceIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteInvoicesSignature {
  /**
   * Deletes multiple invoices.
   *
   *
   * To delete a single invoice, call [Delete Invoice](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/delete-invoice).
   * @param - IDs of invoices to delete.
   */
  (invoiceIds: string[]): Promise<
    NonNullablePaths<
      BulkDeleteInvoicesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateInvoiceTags(
  httpClient: HttpClient
): BulkUpdateInvoiceTagsSignature {
  return (invoiceIds: string[], options?: BulkUpdateInvoiceTagsOptions) =>
    universalBulkUpdateInvoiceTags(
      invoiceIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateInvoiceTagsSignature {
  /**
   * Adds or removes tags on multiple invoices. Tags are labels used to organize and filter invoices — they can be public (visible to anyone with access to the invoice) or private (restricted to site owners).
   *
   * Unlike [Bulk Update Invoices](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/bulk-update-invoices), which replaces fields entirely, this method lets you specify individual tags to assign or unassign without affecting other existing tags. A tag that appears in both the list to assign and the list to unassign is assigned. Tags that don't exist are ignored.
   *
   * To update tags on all invoices matching a filter, call [Bulk Update Invoice Tags By Filter](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/bulk-update-invoice-tags-by-filter).
   * @param - IDs of invoices to update tags for.
   */
  (invoiceIds: string[], options?: BulkUpdateInvoiceTagsOptions): Promise<
    NonNullablePaths<
      BulkUpdateInvoiceTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateInvoiceTagsApplicationErrors;
    }
  >;
}

export function bulkUpdateInvoiceTagsByFilter(
  httpClient: HttpClient
): BulkUpdateInvoiceTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdateInvoiceTagsByFilterOptions
  ) =>
    universalBulkUpdateInvoiceTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateInvoiceTagsByFilterSignature {
  /**
   * Asynchronously updates tags on multiple invoices that match the provided filter criteria.
   *
   *
   * To check the status of the bulk update, pass the returned `jobId` to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/get-async-job).
   *
   * To update tags on specific invoices by ID, call [Bulk Update Invoice Tags](https://dev.wix.com/docs/api-reference/business-management/get-paid/invoices/invoices/bulk-update-invoice-tags).
   * @param - Filter for selecting invoices to update.
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdateInvoiceTagsByFilterOptions
  ): Promise<
    NonNullablePaths<BulkUpdateInvoiceTagsByFilterResponse, `jobId`, 2> & {
      __applicationErrorsType?: BulkUpdateInvoiceTagsByFilterApplicationErrors;
    }
  >;
}

export const onInvoiceCreated = /*#__PURE__*/ EventDefinition(
  'wix.invoices.invoices.v4.invoice_created',
  true,
  (event: InvoiceCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.issueDate' },
            { path: 'entity.dueDate' },
            { path: 'entity.lineItems.customItem.subscriptionInfo.startDate' },
            { path: 'entity.lineItems.catalogItem.subscriptionInfo.startDate' },
            { path: 'entity.shipmentInfo.logistics.deliveryTimeSlot.from' },
            { path: 'entity.shipmentInfo.logistics.deliveryTimeSlot.to' },
            { path: 'entity.payments.createdDate' },
            { path: 'entity.payments.updatedDate' },
            { path: 'entity.payments.paymentDate' },
            {
              path: 'entity.customFields.footerCustomField.content.metadata.createdTimestamp',
            },
            {
              path: 'entity.customFields.footerCustomField.content.metadata.updatedTimestamp',
            },
            { path: 'entity.presetProperties.invoicePreset.createdDate' },
            { path: 'entity.presetProperties.invoicePreset.updatedDate' },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
            },
            { path: 'entity.documentInfo.documentExpirationDate' },
            { path: 'entity.activityInfo.lastSentDate' },
            { path: 'entity.activityInfo.lastViewedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.customerInfo.billingAddress' },
            { path: 'entity.shippingInfo.shippingAddress' },
            { path: 'entity.businessDetails.address' },
            { path: 'entity.shipmentInfo.logistics.pickupDetails.address' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.imageData.image.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.imageData.crop.x',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.imageData.crop.y',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.imageData.crop.width',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.imageData.crop.height',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.videoData.video.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.tocData.fontSize',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
            },
          ],
        },
      ])
    )
)<InvoiceCreatedEnvelope>();
export const onInvoiceDeleted = /*#__PURE__*/ EventDefinition(
  'wix.invoices.invoices.v4.invoice_deleted',
  true,
  (event: InvoiceDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.issueDate' },
            { path: 'entity.dueDate' },
            { path: 'entity.lineItems.customItem.subscriptionInfo.startDate' },
            { path: 'entity.lineItems.catalogItem.subscriptionInfo.startDate' },
            { path: 'entity.shipmentInfo.logistics.deliveryTimeSlot.from' },
            { path: 'entity.shipmentInfo.logistics.deliveryTimeSlot.to' },
            { path: 'entity.payments.createdDate' },
            { path: 'entity.payments.updatedDate' },
            { path: 'entity.payments.paymentDate' },
            {
              path: 'entity.customFields.footerCustomField.content.metadata.createdTimestamp',
            },
            {
              path: 'entity.customFields.footerCustomField.content.metadata.updatedTimestamp',
            },
            { path: 'entity.presetProperties.invoicePreset.createdDate' },
            { path: 'entity.presetProperties.invoicePreset.updatedDate' },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
            },
            { path: 'entity.documentInfo.documentExpirationDate' },
            { path: 'entity.activityInfo.lastSentDate' },
            { path: 'entity.activityInfo.lastViewedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.customerInfo.billingAddress' },
            { path: 'entity.shippingInfo.shippingAddress' },
            { path: 'entity.businessDetails.address' },
            { path: 'entity.shipmentInfo.logistics.pickupDetails.address' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.imageData.image.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.imageData.crop.x',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.imageData.crop.y',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.imageData.crop.width',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.imageData.crop.height',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.videoData.video.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.tocData.fontSize',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
            },
          ],
        },
      ])
    )
)<InvoiceDeletedEnvelope>();
export const onInvoiceSent = /*#__PURE__*/ EventDefinition(
  'wix.invoices.invoices.v4.invoice_invoice_sent',
  true,
  (event: InvoiceSentEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.invoice.createdDate' },
            { path: 'data.invoice.updatedDate' },
            { path: 'data.invoice.issueDate' },
            { path: 'data.invoice.dueDate' },
            {
              path: 'data.invoice.lineItems.customItem.subscriptionInfo.startDate',
            },
            {
              path: 'data.invoice.lineItems.catalogItem.subscriptionInfo.startDate',
            },
            {
              path: 'data.invoice.shipmentInfo.logistics.deliveryTimeSlot.from',
            },
            { path: 'data.invoice.shipmentInfo.logistics.deliveryTimeSlot.to' },
            { path: 'data.invoice.payments.createdDate' },
            { path: 'data.invoice.payments.updatedDate' },
            { path: 'data.invoice.payments.paymentDate' },
            {
              path: 'data.invoice.customFields.footerCustomField.content.metadata.createdTimestamp',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.metadata.updatedTimestamp',
            },
            { path: 'data.invoice.presetProperties.invoicePreset.createdDate' },
            { path: 'data.invoice.presetProperties.invoicePreset.updatedDate' },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
            },
            { path: 'data.invoice.documentInfo.documentExpirationDate' },
            { path: 'data.invoice.activityInfo.lastSentDate' },
            { path: 'data.invoice.activityInfo.lastViewedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'data.invoice.customerInfo.billingAddress' },
            { path: 'data.invoice.shippingInfo.shippingAddress' },
            { path: 'data.invoice.businessDetails.address' },
            {
              path: 'data.invoice.shipmentInfo.logistics.pickupDetails.address',
            },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.imageData.image.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.imageData.crop.x',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.imageData.crop.y',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.imageData.crop.width',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.imageData.crop.height',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.videoData.video.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.audioData.audio.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.tocData.fontSize',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
            },
          ],
        },
      ])
    )
)<InvoiceSentEnvelope>();
export const onInvoiceTagsModified = /*#__PURE__*/ EventDefinition(
  'wix.invoices.invoices.v4.invoice_tags_modified',
  true,
  (event: InvoiceTagsModifiedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.invoice.createdDate' },
            { path: 'data.invoice.updatedDate' },
            { path: 'data.invoice.issueDate' },
            { path: 'data.invoice.dueDate' },
            {
              path: 'data.invoice.lineItems.customItem.subscriptionInfo.startDate',
            },
            {
              path: 'data.invoice.lineItems.catalogItem.subscriptionInfo.startDate',
            },
            {
              path: 'data.invoice.shipmentInfo.logistics.deliveryTimeSlot.from',
            },
            { path: 'data.invoice.shipmentInfo.logistics.deliveryTimeSlot.to' },
            { path: 'data.invoice.payments.createdDate' },
            { path: 'data.invoice.payments.updatedDate' },
            { path: 'data.invoice.payments.paymentDate' },
            {
              path: 'data.invoice.customFields.footerCustomField.content.metadata.createdTimestamp',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.metadata.updatedTimestamp',
            },
            { path: 'data.invoice.presetProperties.invoicePreset.createdDate' },
            { path: 'data.invoice.presetProperties.invoicePreset.updatedDate' },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
            },
            { path: 'data.invoice.documentInfo.documentExpirationDate' },
            { path: 'data.invoice.activityInfo.lastSentDate' },
            { path: 'data.invoice.activityInfo.lastViewedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'data.invoice.customerInfo.billingAddress' },
            { path: 'data.invoice.shippingInfo.shippingAddress' },
            { path: 'data.invoice.businessDetails.address' },
            {
              path: 'data.invoice.shipmentInfo.logistics.pickupDetails.address',
            },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.imageData.image.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.imageData.crop.x',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.imageData.crop.y',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.imageData.crop.width',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.imageData.crop.height',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.videoData.video.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.audioData.audio.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.tocData.fontSize',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
            },
            {
              path: 'data.invoice.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
            },
            {
              path: 'data.invoice.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
            },
          ],
        },
      ])
    )
)<InvoiceTagsModifiedEnvelope>();
export const onInvoiceUpdated = /*#__PURE__*/ EventDefinition(
  'wix.invoices.invoices.v4.invoice_updated',
  true,
  (event: InvoiceUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.issueDate' },
            { path: 'entity.dueDate' },
            { path: 'entity.lineItems.customItem.subscriptionInfo.startDate' },
            { path: 'entity.lineItems.catalogItem.subscriptionInfo.startDate' },
            { path: 'entity.shipmentInfo.logistics.deliveryTimeSlot.from' },
            { path: 'entity.shipmentInfo.logistics.deliveryTimeSlot.to' },
            { path: 'entity.payments.createdDate' },
            { path: 'entity.payments.updatedDate' },
            { path: 'entity.payments.paymentDate' },
            {
              path: 'entity.customFields.footerCustomField.content.metadata.createdTimestamp',
            },
            {
              path: 'entity.customFields.footerCustomField.content.metadata.updatedTimestamp',
            },
            { path: 'entity.presetProperties.invoicePreset.createdDate' },
            { path: 'entity.presetProperties.invoicePreset.updatedDate' },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
            },
            { path: 'entity.documentInfo.documentExpirationDate' },
            { path: 'entity.activityInfo.lastSentDate' },
            { path: 'entity.activityInfo.lastViewedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.issueDate' },
            { path: 'modifiedFields.dueDate' },
            {
              path: 'modifiedFields.lineItems.customItem.subscriptionInfo.startDate',
            },
            {
              path: 'modifiedFields.lineItems.catalogItem.subscriptionInfo.startDate',
            },
            {
              path: 'modifiedFields.shipmentInfo.logistics.deliveryTimeSlot.from',
            },
            {
              path: 'modifiedFields.shipmentInfo.logistics.deliveryTimeSlot.to',
            },
            { path: 'modifiedFields.payments.createdDate' },
            { path: 'modifiedFields.payments.updatedDate' },
            { path: 'modifiedFields.payments.paymentDate' },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.metadata.createdTimestamp',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.metadata.updatedTimestamp',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.createdDate',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.updatedDate',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.metadata.createdTimestamp',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.metadata.updatedTimestamp',
            },
            { path: 'modifiedFields.documentInfo.documentExpirationDate' },
            { path: 'modifiedFields.activityInfo.lastSentDate' },
            { path: 'modifiedFields.activityInfo.lastViewedDate' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.customerInfo.billingAddress' },
            { path: 'entity.shippingInfo.shippingAddress' },
            { path: 'entity.businessDetails.address' },
            { path: 'entity.shipmentInfo.logistics.pickupDetails.address' },
            { path: 'modifiedFields.customerInfo.billingAddress' },
            { path: 'modifiedFields.shippingInfo.shippingAddress' },
            { path: 'modifiedFields.businessDetails.address' },
            {
              path: 'modifiedFields.shipmentInfo.logistics.pickupDetails.address',
            },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.imageData.image.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.imageData.crop.x',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.imageData.crop.y',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.imageData.crop.width',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.imageData.crop.height',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.videoData.video.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.tocData.fontSize',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
            },
            {
              path: 'entity.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.imageData.image.duration',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.imageData.crop.x',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.imageData.crop.y',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.imageData.crop.width',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.imageData.crop.height',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.pollData.poll.image.duration',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.appEmbedData.image.duration',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.videoData.video.duration',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.audioData.audio.duration',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.audioData.coverImage.duration',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerX',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.centerY',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.shapeData.shape.duration',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.tocData.fontSize',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.tocData.itemSpacing',
            },
            {
              path: 'modifiedFields.customFields.footerCustomField.content.nodes.smartBlockCellData.shape.duration',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.galleryData.options.item.ratio',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.image.duration',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.x',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.y',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.width',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.imageData.crop.height',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lat',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.mapData.mapSettings.lng',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.image.duration',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.pollData.design.poll.background.image.duration',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.appEmbedData.image.duration',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.video.duration',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.videoData.thumbnail.duration',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.audio.duration',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.audioData.coverImage.duration',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerX',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.centerY',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.layoutData.backdrop.gradient.stops.position',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.shapeData.shape.duration',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.media.duration',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerX',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.centerY',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.fontSize',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.tocData.itemSpacing',
            },
            {
              path: 'modifiedFields.presetProperties.invoicePreset.customFields.footerCustomField.nodes.smartBlockCellData.shape.duration',
            },
          ],
        },
      ])
    )
)<InvoiceUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  Action,
  ActionEvent,
  ActivityInfo,
  AddPaymentRequest,
  AddPaymentResponse,
  AdditionalFee,
  AdditionalFeeSource,
  Address,
  AddressLocation,
  Aggregation,
  AggregationData,
  AggregationKindOneOf,
  AggregationResults,
  AggregationResultsResultOneOf,
  AggregationResultsScalarResult,
  AggregationType,
  Alignment,
  AnchorData,
  Animation,
  AppEmbedData,
  AppEmbedDataAppDataOneOf,
  AppType,
  ApplicationError,
  ArchiveInvoiceRequest,
  ArchiveInvoiceResponse,
  AspectRatio,
  Attachment,
  AudioData,
  AutoCharge,
  Backdrop,
  BackdropType,
  Background,
  BackgroundGradient,
  BackgroundImage,
  BackgroundType,
  Banner,
  BannerPosition,
  BaseEventMetadata,
  BlockquoteData,
  BookingData,
  Border,
  BorderColors,
  BorderWidths,
  BulkActionMetadata,
  BulkCreateInvoicesOptions,
  BulkCreateInvoicesRequest,
  BulkCreateInvoicesResponse,
  BulkDeleteInvoicesRequest,
  BulkDeleteInvoicesResponse,
  BulkDeleteInvoicesResponseBulkInvoiceResult,
  BulkInvoiceResult,
  BulkUpdateInvoiceTagsByFilterOptions,
  BulkUpdateInvoiceTagsByFilterRequest,
  BulkUpdateInvoiceTagsByFilterResponse,
  BulkUpdateInvoiceTagsOptions,
  BulkUpdateInvoiceTagsRequest,
  BulkUpdateInvoiceTagsResponse,
  BulkUpdateInvoiceTagsResult,
  BulkUpdateInvoicesOptions,
  BulkUpdateInvoicesRequest,
  BulkUpdateInvoicesResponse,
  BulkUpdateInvoicesResponseBulkInvoiceResult,
  BulletedListData,
  BusinessDetails,
  BusinessDisplayOptions,
  BusinessLocation,
  ButtonData,
  ButtonDataType,
  ButtonStyles,
  CalculateInvoiceOptions,
  CalculateInvoiceRequest,
  CalculateInvoiceResponse,
  CalculationErrors,
  CalculationErrorsShippingCalculationErrorOneOf,
  CaptionData,
  CardData,
  CardDataBackground,
  CardDataBackgroundType,
  CardStyles,
  CardStylesType,
  CarrierError,
  CarrierErrors,
  CashRounding,
  CatalogItem,
  CatalogReference,
  CellStyle,
  CheckboxListData,
  CodeBlockData,
  CollapsibleListData,
  ColorData,
  Colors,
  ColumnSize,
  ConvertInvoiceRequest,
  ConvertInvoiceResponse,
  CreateInvoiceRequest,
  CreateInvoiceResponse,
  CreditCardDetails,
  Crop,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  CursorSearch,
  CursorSearchPagingMethodOneOf,
  Cursors,
  CustomField,
  CustomFieldPlaceholder,
  CustomFields,
  CustomItem,
  CustomerDisplayOptions,
  CustomerInfo,
  Cycle,
  CycleCycleOptionsOneOf,
  DateHistogramAggregation,
  DateHistogramResult,
  DateHistogramResults,
  Decoration,
  DecorationDataOneOf,
  DecorationType,
  DeleteInvoiceRequest,
  DeleteInvoiceResponse,
  DeliveryLogistics,
  DeliveryLogisticsOption,
  DeliveryTimeSlot,
  Deposit,
  DepositDepositOptionsOneOf,
  DepositType,
  Design,
  DesignTarget,
  Details,
  DetailsKindOneOf,
  Dimensions,
  Direction,
  Discount,
  DiscountDiscountOptionsOneOf,
  DiscountType,
  DisplaySettings,
  DisplayValues,
  DividerData,
  DividerDataAlignment,
  DividerDataStyles,
  DocumentInfo,
  DocumentStatus,
  DocumentStyle,
  DomainCustomField,
  DomainCustomFields,
  DomainEvent,
  DomainEventBodyOneOf,
  EmbedData,
  Empty,
  EnableInvoicePaymentsRequest,
  EnableInvoicePaymentsResponse,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EntranceAnimation,
  EntranceEffect,
  EntranceEffectType,
  ErrorType,
  EventData,
  EventMetadata,
  ExternalReceipt,
  Failed,
  FieldViolation,
  FileData,
  FileSource,
  FileSourceDataOneOf,
  FileState,
  FontFamilyData,
  FontSizeData,
  FontType,
  FooterContent,
  FullAddressContactDetails,
  GIF,
  GIFData,
  GIFType,
  GalleryData,
  GalleryOptions,
  GalleryOptionsLayout,
  GeneratePdfDocumentRequest,
  GeneratePdfDocumentResponse,
  GenerateReceiptRequest,
  GenerateReceiptResponse,
  GetInvoiceRequest,
  GetInvoiceResponse,
  GetLatestInvoiceNumberOptions,
  GetLatestInvoiceNumberRequest,
  GetLatestInvoiceNumberResponse,
  GiftCardPaymentDetails,
  Gradient,
  GradientType,
  GroupByAggregation,
  GroupByAggregationKindOneOf,
  GroupByValueResults,
  HTMLData,
  HTMLDataDataOneOf,
  HeadingData,
  Height,
  IdentificationData,
  IdentificationDataIdOneOf,
  IllegalActionErrorData,
  Image,
  ImageData,
  ImageDataCrop,
  ImageDataStyles,
  ImagePosition,
  ImagePositionPosition,
  ImageScalingScaling,
  ImageStyles,
  IncludeMissingValuesOptions,
  Indentation,
  InitialExpandedItems,
  InitiatePaymentRequest,
  InitiatePaymentResponse,
  InternalCountInvoicesRequest,
  InternalCountInvoicesResponse,
  Interval,
  Invoice,
  InvoiceCreatedEnvelope,
  InvoiceDeletedEnvelope,
  InvoiceDocumentHandled,
  InvoiceDocumentHandledResultOneOf,
  InvoiceDocumentProcess,
  InvoiceNumberingProcess,
  InvoicePreset,
  InvoiceQuerySpec,
  InvoiceSearchSpec,
  InvoiceSent,
  InvoiceSentEnvelope,
  InvoiceTagsModifiedEnvelope,
  InvoiceUpdatedEnvelope,
  InvoicesQueryBuilder,
  InvoicesQueryResult,
  Item,
  ItemDataOneOf,
  ItemMetadata,
  ItemStyle,
  ItemType,
  ItemTypeItemTypeDataOneOf,
  ItemsDisplayOptions,
  LayerEffect,
  Layers,
  Layout,
  LayoutCellData,
  LayoutData,
  LayoutDataBackground,
  LayoutDataBackgroundImage,
  LayoutDataBackgroundType,
  LayoutType,
  LineCap,
  LineItem,
  LineItemDiscount,
  LineItemDiscountDiscountOptionsOneOf,
  LineItemDiscountDiscountType,
  LineItemLineItemOptionsOneOf,
  LineItemTaxBreakdown,
  LineItemTaxInfo,
  LineItemTotals,
  LineItemType,
  LineStyle,
  Link,
  LinkData,
  LinkDataOneOf,
  LinkPreviewData,
  LinkPreviewDataStyles,
  Links,
  ListItemNodeData,
  ListStyle,
  ListValue,
  Locale,
  LoopAnimation,
  LoopEffect,
  LoopEffectType,
  MapData,
  MapSettings,
  MapType,
  MarkInvoiceAsSentRequest,
  MarkInvoiceAsSentResponse,
  MarkInvoiceAsViewedRequest,
  MarkInvoiceAsViewedResponse,
  MaskedInvoice,
  Media,
  MediaFileStateChanged,
  MentionData,
  MessageEnvelope,
  Metadata,
  Migrated,
  MissingValues,
  Mode,
  Monthly,
  NestedAggregation,
  NestedAggregationItem,
  NestedAggregationItemKindOneOf,
  NestedAggregationResults,
  NestedAggregationResultsResultOneOf,
  NestedAggregationType,
  NestedResultValue,
  NestedResultValueResultOneOf,
  NestedResults,
  NestedValueAggregationResult,
  Node,
  NodeDataOneOf,
  NodeStyle,
  NodeType,
  NullValue,
  Numbering,
  Oembed,
  Option,
  OptionDesign,
  OptionLayout,
  Order,
  OrderedListData,
  Orientation,
  Origin,
  PDFSettings,
  PagingMetadataV2,
  ParagraphData,
  Payment,
  PaymentPaymentOptionsOneOf,
  PaymentReceiptOptionsOneOf,
  PaymentStatus,
  PaymentType,
  PaymentsDisplayOptions,
  Permissions,
  PickupDetails,
  PickupMethod,
  Placement,
  PlaybackOptions,
  PluginContainerData,
  PluginContainerDataAlignment,
  PluginContainerDataWidth,
  PluginContainerDataWidthDataOneOf,
  PointerEffect,
  PointerEffectType,
  Poll,
  PollData,
  PollDataLayout,
  PollDesign,
  PollDesignBackground,
  PollDesignBackgroundBackgroundOneOf,
  PollDesignBackgroundType,
  PollLayout,
  PollLayoutDirection,
  PollLayoutType,
  Position,
  PresetProperties,
  PresetType,
  PricingData,
  PublishInvoiceRequest,
  PublishInvoiceResponse,
  QueryInvoicesRequest,
  QueryInvoicesResponse,
  RangeAggregation,
  RangeAggregationResult,
  RangeBucket,
  RangeResult,
  RangeResults,
  ReceiptType,
  Reference,
  ReferenceReferenceOptionsOneOf,
  ReferenceType,
  RegionalProperties,
  RegularPaymentDetails,
  Rel,
  Resizing,
  ResponsivenessBehaviour,
  RestoreInfo,
  Results,
  RibbonStyles,
  RichContent,
  RuleType,
  ScalarAggregation,
  ScalarResult,
  ScalarType,
  Scaling,
  SearchDetails,
  SearchInvoicesRequest,
  SearchInvoicesResponse,
  SectionDivider,
  SendErrorType,
  SendInvoiceOptions,
  SendInvoiceRequest,
  SendInvoiceResponse,
  SendResult,
  Settings,
  Shape,
  ShapeData,
  ShapeDataStyles,
  ShipmentInfo,
  ShipmentOption,
  ShipmentRegion,
  ShipmentTaxBreakdown,
  ShipmentTaxInfo,
  ShipmentTotals,
  ShippingInfo,
  SideEffectsTriggerRequested,
  SiteTaxesMigrationRequested,
  SketchData,
  SmartBlockCellData,
  SmartBlockData,
  SmartBlockDataType,
  SortDirection,
  SortOrder,
  SortType,
  Sorting,
  Source,
  SourceReference,
  Spoiler,
  SpoilerData,
  Standalone,
  Status,
  StatusQualifier,
  Stop,
  StreetAddress,
  Styles,
  StylesBorder,
  StylesPosition,
  SubscriptionInfo,
  Succeeded,
  SystemError,
  TableCellData,
  TableData,
  TagList,
  Tags,
  TagsModified,
  Target,
  TaxBreakdown,
  TaxInfo,
  TextAlignment,
  TextData,
  TextNodeStyle,
  TextStyle,
  Thumbnails,
  ThumbnailsAlignment,
  TocData,
  Totals,
  TotalsDisplayOptions,
  Type,
  UnarchiveInvoiceRequest,
  UnarchiveInvoiceResponse,
  UpdateInvoice,
  UpdateInvoiceRequest,
  UpdateInvoiceResponse,
  ValidationError,
  ValueAggregation,
  ValueAggregationOptionsOneOf,
  ValueAggregationResult,
  ValueResult,
  ValueResults,
  Variant,
  VatId,
  VatType,
  VerticalAlignment,
  VerticalAlignmentAlignment,
  Video,
  VideoData,
  ViewMode,
  ViewRole,
  VoidInvoiceRequest,
  VoidInvoiceResponse,
  VoteRole,
  WebhookIdentityType,
  Weekly,
  Width,
  WidthType,
  WixReceipt,
  Yearly,
  utils,
} from './invoices-invoices-v4-invoice-invoices.universal.js';
